import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  View,
  Text,
  ScrollView,
  useWindowDimensions,
  Pressable,
  Image,
  Alert,
  StyleSheet,
} from "react-native";
import {
  ArrowLeft,
  Activity,
  History,
  Syringe,
  Beaker,
  Target,
  Sparkles,
  Baby,
  Hourglass,
  CalendarClock,
  ClipboardList,
  Pencil,
  Plus,
  ChevronRight,
  XCircle,
  HeartHandshake,
  type LucideIcon,
} from "lucide-react-native";
import { useSafeAreaInsets } from "react-native-safe-area-context";
import type { NativeStackScreenProps } from "@react-navigation/native-stack";
import { useFocusEffect } from "@react-navigation/native";
import type { NavigationProp } from "@react-navigation/native";
import { useQueryClient } from "@tanstack/react-query";
import type { CareStackParamList, MainTabParamList } from "../types";
import type { RootStackParamList } from "../../App";
import endingIllustration from "../../assets/images/end-treatment-cycle-illustration.png";
import { useDocumentUploadController } from "../DocumentUploadController";
import GlassScreenLayout from "@louise/moon-ui/primitives/GlassScreenLayout";
import {
  Dialog,
  DialogActions,
  DialogHeader,
  DialogIcon,
  DialogPrimaryButton,
  DialogTitleText,
  ActionRowCard,
  SectionTitle,
  KebabMenu,
  type KebabMenuItem,
} from "@louise/moon-ui";
import {
  SCREEN_PADDING_TOP,
  semanticColors,
} from "@louise/moon-ui/theme/tokens";
import { toLocalDateString, getTodayString } from "@src/packages/utils/date";
import { DEV_STEP_STATES_OVERRIDE } from "@src/packages/cycles/dev/devActiveCycleOverride";
import {
  outcomeNoteNeedsWriting,
  outcomeWriteFor,
} from "@src/packages/cycles/presenters/outcomePatch";
import MedicationFactsheetSheet from "../components/MedicationFactsheetSheet";
import ProcedureDetailSheet from "../components/ProcedureDetailSheet";
import { getProcedureByName } from "@src/packages/content";
import { CycleIllustration } from "../components/CycleIllustration";
import {
  useActiveCycleWithPhases,
  useCycles,
  useCycleTypeLabels,
  useUpdateCycle,
  useCompleteCycle,
  useRecordCycleOutcome,
  CYCLE_TYPE_LABELS,
  PHASE_TYPE_LABELS,
  isDevNavigableCycleId,
  type Phase,
  type CycleOutcomeResultStatus,
} from "@src/packages/cycles/hooks/useCycles";
import { useCreateTreatmentSnapshot } from "@src/packages/cycles/hooks/usePastCycleDetail";
import {
  usePhases,
  deriveStepProgress,
  getPhaseTypeForStep,
  buildFertilityMetrics,
  buildPhaseSubObjects,
  buildPgtTestResult,
  pgtTestResultMatches,
  phaseKeys,
  type ApiPhase,
  type ApiFertilityMetric,
} from "@src/packages/patients/hooks/usePhases";
import { hasConfirmedPregnancyProcedure } from "@src/packages/cycles/ivfFlowGuards";
import { useApiClient } from "@src/packages/api/louise-api";
import {
  buildBetaTestResultPayload,
  buildBetaTestResultUpdate,
  PATIENT_BETA_OUTCOME_SOURCE_KIND,
} from "@src/packages/cycles/presenters/betaTestResult";
import UpdatePregnancyModal from "./care/modals/UpdatePregnancyModal";
import { WhatToKnowView } from "@src/packages/homepage/plan/components/HomepageReviewConceptHome";
import { buildEarlyPregnancyWhatToKnowCards } from "@src/packages/homepage/plan/earlyPregnancyCards";
import { getPostProcedureOutcomeContext } from "@src/packages/cycles/presenters/postProcedureOutcome";
import {
  statePregnancyStatus,
  summarisePregnancyStatus,
  type PregnancyStatus,
} from "@src/packages/cycles/presenters/pregnancyStatusUpdate";
import {
  outcomeWriteTarget,
  resolveOutcomeWriteRequestId,
  endingAfterBetaWrite,
  betaWritableEnding,
  type OutcomeWriteRequestId,
} from "@src/packages/cycles/presenters/patientOutcomeWrite";
import { betaResultErrorMessage } from "./care/modals/betaResultErrorMessage";
import {
  getStepsForCycle,
  type TreatmentStepConfig,
  type StepPerspective,
} from "@src/packages/homepage/sections/TreatmentProgressModal";
import { findLatestPerformedProcedurePhase } from "@src/packages/cycles/presenters/performedProcedure";
import { stepMayCreatePhase } from "@src/packages/cycles/presenters/stepPhaseWrite";
import { completionMayBeCelebrated } from "@src/packages/cycles/presenters/completionTone";
import {
  DAY_IN_MS,
  estimateTimelineWindow,
  resolveOutcomeTiming,
} from "@src/packages/cycles/presenters/timelineWindow";
import {
  FETBetaOrderUnavailableError,
  FETBetaOrderLookupPendingError,
  getFETBetaOrderDate,
  isFETWaitEligible,
  fetBetaLifecycleQueryKey,
  selectLatestFETBetaOrder,
  selectRecordableFETBetaOrder,
  useCreateFETBetaOrder,
  useFETBetaLifecycle,
  useRecordFETBetaResult,
} from "@src/packages/cycles/useFETBetaLifecycle";
import {
  isPatientOwnedFETProcedure,
  isReportedFETTransfer,
} from "@src/packages/cycles/fetProcedureOwnership";
import { useFETPlanForCycle } from "@src/packages/cycles/useFETPlan";
import {
  useReportFETTransferSelf,
  useUpdateFETProcedureReport,
} from "@src/packages/cycles/useFETProcedure";
import { isDurationPhaseType } from "@src/packages/cycles/presenters/phaseSemantics";
import {
  isCurrentPartnerCareViewer,
  shouldUsePartnerCyclePerspective,
} from "@src/packages/cycles/partnerCycleContext";
import type {
  TreatmentStepProgress,
  CycleType,
} from "@louise/care/treatment-phases";
import {
  MoonTreatmentHubCard,
  type MoonTreatmentHubCardHandle,
  HubCycle,
} from "./care/sections/treatment-hub";
import {
  getProcedureAppointmentPrefill,
  getProcedureSchedulingEntry,
  findScheduledProcedureStep,
} from "./care/sections/treatment-hub/procedureScheduling";
import { AddMedicationModal } from "./care/modals/AddMedicationModal";
import type { EditMedicationData } from "./care/modals/AddMedicationModal";
import {
  deriveRecurrence,
  type AppointmentDetailItem,
  type MedicationScheduleItem,
} from "./care/schedule.types";
import CycleProtocolSection, {
  type CycleProtocolDoseLog,
  type CycleProtocolData,
  type CycleProtocolMedication,
  type CycleProtocolMedicationPeriod,
} from "./care/sections/CycleProtocolSection";
import CycleRelatedDocumentsSection, {
  getCycleDocumentFilterLabel,
  getCycleDocuments,
  getCycleDocumentTag,
} from "./care/sections/CycleRelatedDocumentsSection";
import CycleTestsSection from "./care/sections/CycleTestsSection";
import OvarianStimulationMonitoringSection from "./care/sections/OvarianStimulationMonitoringSection";
import { isMonitoredCycleType } from "@src/packages/cycles";
import {
  CalendarOverviewCard,
  useCalendarLoggingTabBarOverride,
  type CalendarSymptomOverlayState,
} from "./care/suggested/CareReviewConceptScreen";
import { MoonStartTreatmentModal } from "./care/modals/MoonStartTreatmentModal";
import {
  createClientRequestId,
  useAcceptEmbryoFreezingPlan,
  useCreateEmbryoFreezingPlan,
  useEmbryoFreezingPlan,
} from "@src/packages/embryo-freezing-plans/hooks/useEmbryoFreezingPlans";
import { createMinimalEmbryoFreezingPlanInput } from "@src/packages/embryo-freezing-plans/minimalStartPlan";
import { AddAppointmentModal } from "./care/modals/AddAppointmentModal";
import { AppointmentDetailSheet } from "./care/modals/AppointmentDetailSheet";
import EndCycleModal, {
  type PreservationOutcomeCounts,
} from "./YouScreen/modals/EndCycleModal";
import {
  createIdempotencyKey,
  useFrozenSpecimensForCycle,
  useOocyteCohorts,
  useSpecimens,
} from "@src/packages/care/cryovault/useSpecimens";
import { getFrozenSpecimenSelectionForCycle } from "@src/packages/care/cryovault/frozenCycleSelection";
import {
  buildCryoSpecimenSnapshotsFromStorageGroups,
  CryoVaultOverviewCard,
  type CryoSpecimenSnapshot,
} from "@src/packages/care/cryovault/CryoVaultOverviewCard";
import {
  CryoSpecimenDetailModal,
  buildCryoSpecimenLabel,
  mapApiSpecimensToCryoDetails,
  type CryoSpecimenDetail,
} from "../components/CryoSpecimenDetailModal";
import CycleClinicTeamCard, {
  buildCycleTeamMembers,
} from "./care/sections/CycleClinicTeamCard";
import {
  AddSpecimenModal,
  type SpecimenPrefill,
} from "./care/modals/AddSpecimenModal";
import { resolvePartnerCounterparty } from "@src/packages/patients/resolvePartnerCounterparty";
import { resolveProcedureAppointmentPlan } from "@src/packages/cycles/presenters/procedureAppointmentSync";
import { RecordBetaResultModal } from "./care/modals/RecordBetaResultModal";
import CycleCareTeamModal from "./care/modals/CycleCareTeamModal";
import { WaitingPhaseView } from "./care/sections/treatment-hub/WaitingPhaseView";
import { FETBetaResultSeries } from "./care/sections/treatment-hub/FETBetaResultSeries";
import { RecordedResultView } from "./care/sections/treatment-hub/RecordedResultView";
import type {
  StartTreatmentFormData,
  EditTreatmentData,
} from "./care/modals/MoonStartTreatmentModal";
import { usePatient } from "@src/packages/patients/hooks/usePatient";
import { useCreateCycle } from "@src/packages/cycles/hooks/useCycles";
import {
  useCreatePhase,
  useUpdatePhase,
} from "@src/packages/patients/hooks/usePhases";
import { usePatientMedications } from "@src/packages/care/hooks/usePatientMedications";
import {
  useCreateTestResult,
  useTestResults,
  useUpdateTestResult,
} from "@src/packages/test-results/hooks/useTestResults";
import { appointmentKeys } from "@src/packages/care/hooks/useAppointments";
import {
  calendarKeys,
  useCreateAppointment,
  useDeleteAppointment,
  useMedicationDoseLogs,
  useUpdateAppointment,
} from "@src/packages/care/useCalendarData";
import { usePatientNotes } from "@src/packages/care/hooks/usePatientNotes";
import type {
  HomepageMedicationFactSheetPayload,
  HomepageProcedureFactSheetPayload,
} from "@src/packages/homepage/plan/components/HomepageReviewConceptHome";
import { useDocuments } from "@src/packages/documents/hooks/useDocuments";
import { useCareTeam } from "@src/packages/care/hooks/useCareTeam";
import { useTranslations, type TranslationFunction } from "@src/packages/i18n";
import { useLocale } from "@src/packages/i18n/useLocale";
import type { Locale } from "@src/packages/i18n/locale";
import { formatDate, formatDateTime } from "@src/packages/i18n/formatters";
import { useCycleHeaderState } from "@src/packages/cycles/hooks/useCycleHeaderState";
import { presentCycleHeaderForCare } from "@src/packages/cycles/presenters/cycleHeaderCare";
import { useSmartBack } from "../hooks/useSmartBack";
import ScreenHeader from "../components/ScreenHeader";
import AddPastCycleDialog from "./care/modals/AddPastCycleDialog";
import { useCycleNavigation } from "../hooks/useCycleNavigation";
import { ArticleRailCard } from "../components/ArticleSectionCard";
import { NegativeCloseoutSupport } from "./care/modals/NegativeCloseoutSupport";
import { getPostCycleArticles } from "./care/postCycleArticles";
import { OrganicExplanatoryIllustration } from "@src/packages/homepage/plan/components/EggFreezingBiologyDeepCarouselCard";
import {
  useCyclePregnancyFollowUp,
  useUpdateCyclePregnancyFollowUp,
  buildEarlyPregnancyDating,
  gestationFromReportedWeeks,
} from "@src/packages/homepage/pregnancy";

import {
  pressLabel,
  usePressCapture,
} from "@src/packages/analytics/usePressCapture";
function isDevCryoBridgeFixture(cycleId?: string | null) {
  if (!__DEV__) return false;
  if (isDevNavigableCycleId(cycleId)) return true;
  if (typeof window === "undefined") return false;
  return (
    new URLSearchParams(window.location.search).get(
      "dev_cycle_detail_scenario",
    ) === "cryo_bridge"
  );
}

const DEV_CRYO_BRIDGE_SPECIMENS: CryoSpecimenDetail[] = [
  {
    id: "dev-cycle-egg-1",
    type: "oocyte",
    label: "Egg #1",
    facility: "Kindbody San Francisco",
    status: "stored",
    cycleId: "dev-cycle-bridge",
    cycleLabel: "Egg freezing #1",
    frozenDate: "2026-02-12",
    source: "self",
    storageId: "Tank 2 - Cane 4",
    oocyteRecordKind: "individual",
  },
  {
    id: "dev-cycle-embryo-1",
    type: "embryo",
    label: "Embryo #1",
    facility: "Kindbody San Francisco",
    status: "stored",
    cycleId: "dev-cycle-bridge",
    cycleLabel: "Egg freezing #1",
    frozenDate: "2026-02-18",
    source: "self",
    sourceEggId: "dev-cycle-egg-1",
    grade: "5AA",
    pgtResult: "untested",
    transferCandidate: true,
    storageId: "Tank 2 - Cane 5",
  },
  {
    id: "dev-cycle-sperm-1",
    type: "sperm",
    label: "Semen sample #1",
    facility: "NY Fertility Center",
    status: "stored",
    cycleId: "dev-cycle-bridge",
    cycleLabel: "Egg freezing #1",
    frozenDate: "2026-02-10",
    source: "partner",
    storageId: "Tank 1 - Cane 2",
  },
];

const DEV_CRYO_BRIDGE_SNAPSHOTS: CryoSpecimenSnapshot[] = [
  {
    id: "dev-cycle-egg-group",
    label: "Egg",
    count: "1",
    facility: "Kindbody San Francisco",
    renewal: "Not set",
    detail: "Stored Feb 12, 2026",
    status: "Stored",
    organicIcon: "eggs",
    specimenIds: ["dev-cycle-egg-1"],
  },
  {
    id: "dev-cycle-embryo-group",
    label: "Embryo",
    count: "1",
    facility: "Kindbody San Francisco",
    renewal: "Not set",
    detail: "Stored Feb 18, 2026",
    status: "Stored",
    organicIcon: "genetics",
    specimenIds: ["dev-cycle-embryo-1"],
  },
  {
    id: "dev-cycle-sperm-group",
    label: "Sperm sample",
    count: "1",
    facility: "NY Fertility Center",
    renewal: "Not set",
    detail: "Stored Feb 10, 2026",
    status: "Stored",
    organicIcon: "sperm",
    specimenIds: ["dev-cycle-sperm-1"],
  },
];

type Props = NativeStackScreenProps<CareStackParamList, "CycleDetail">;

// ─── EMBER PALETTE ───────────────────────────────────────────

/**
 * The window shown after a first pregnancy announcement wears the same
 * illustration as the pregnancy-follow-up dialog. Hoisted rather than required
 * inside JSX, which the repo forbids and this file's neighbours avoid.
 */
const PREGNANCY_RECAP_ILLUSTRATION =
  require("../../assets/images/calendar-reminder-dialog-illustration.png") as number;

const WARM = "#E8875C";
const WARM_LIGHT = "#FDDAB0";
function normalizeMedicationTimes(value: unknown): string[] {
  if (Array.isArray(value)) {
    return value.filter((time): time is string => typeof time === "string");
  }
  if (typeof value === "string" && value.trim()) {
    return [value.trim()];
  }
  return [];
}

function formatMedicationDose(
  dose: number | string | null | undefined,
  doseUnit: string | null | undefined,
) {
  if (dose === null || dose === undefined || dose === "") return "";
  return `${dose}${doseUnit ? ` ${doseUnit}` : ""}`.trim();
}

function getCycleDayLabel(
  cycleStartDate: string | null | undefined,
  date: string | null | undefined,
  t: TranslationFunction,
) {
  if (!cycleStartDate || !date) return undefined;
  const start = new Date(`${cycleStartDate}T00:00:00`).getTime();
  const target = new Date(`${date}T00:00:00`).getTime();
  if (!Number.isFinite(start) || !Number.isFinite(target)) return undefined;
  const dayNum = Math.max(1, Math.round((target - start) / DAY_IN_MS) + 1);
  return t("care.cycle_detail.timeline.day_label", {
    params: { day: String(dayNum) },
  });
}

function getMedicationTiming(
  times: string[],
): MedicationScheduleItem["timing"] {
  if (times.length === 0) return "as-needed";
  const hours = times
    .map((time) => parseInt(time, 10))
    .filter((hour) => Number.isFinite(hour));
  if (hours.length === 0) return "as-needed";
  if (hours.every((hour) => hour >= 12)) return "evening";
  if (hours.every((hour) => hour < 12)) return "morning";
  return "as-needed";
}

// ─── TYPE DEFINITIONS (used by sub-components) ──────────────

/**
 * Not a rendered timeline, despite the name.
 *
 * Its two readers take `timing` and `status` and nothing else — the hub's step
 * badge and its "you are here" pointer. It once carried a `title`, an
 * `isEstimate` flag and an `onPress`, all computed on every render and read by
 * nobody.
 */
type CycleChartTimelineItem = {
  stepId: string;
  timing: string;
  status: "completed" | "active" | "upcoming";
};

type CycleChartTimelineStepConfig = {
  id: string;
  title: string;
  shortTitle?: string;
};

const OVARIAN_CYCLE_TYPES = new Set<string>([
  "egg_freezing",
  "embryo_freezing",
  "ivf",
  "icsi",
]);

const EMBRYO_LAB_CYCLE_TYPES = new Set<string>([
  "embryo_freezing",
  "ivf",
  "icsi",
]);

function getLocalDateMs(date?: string | null) {
  if (!date) return null;
  const ms = new Date(date.includes("T") ? date : `${date}T00:00:00`).getTime();
  return Number.isNaN(ms) ? null : ms;
}

function formatTimelineDate(ms: number, locale: Locale) {
  return formatDate(ms, locale, {
    month: "short",
    day: "numeric",
  });
}

function formatTimelineWindow(startMs: number, endMs: number, locale: Locale) {
  const start = formatTimelineDate(startMs, locale);
  const end = formatTimelineDate(endMs, locale);
  return start === end ? start : `${start} – ${end}`;
}

function formatPhaseTiming(
  phase: Phase | null | undefined,
  status: CycleChartTimelineItem["status"],
  todayStr: string,
  t: TranslationFunction,
  locale: Locale,
) {
  const startMs = getLocalDateMs(phase?.startDate);
  const endMs = getLocalDateMs(phase?.endDate);
  if (!startMs) return null;

  if (status === "completed" && endMs) {
    return formatTimelineWindow(startMs, endMs, locale);
  }

  if (status === "active") {
    const todayMs = getLocalDateMs(todayStr) ?? Date.now();
    if (!isDurationPhaseType(phase?.type)) {
      return t("care.cycle_detail.timeline.started_on", {
        params: { date: formatTimelineDate(startMs, locale) },
      });
    }
    const dayNum = Math.max(1, Math.round((todayMs - startMs) / DAY_IN_MS) + 1);
    return t("care.cycle_detail.timeline.started_with_day", {
      params: {
        date: formatTimelineDate(startMs, locale),
        day: String(dayNum),
      },
    });
  }

  if (endMs && endMs !== startMs) {
    return formatTimelineWindow(startMs, endMs, locale);
  }

  return t("care.cycle_detail.timeline.starts", {
    params: { date: formatTimelineDate(startMs, locale) },
  });
}

function insertTimelineStepAfter(
  steps: CycleChartTimelineStepConfig[],
  anchorIds: string[],
  step: CycleChartTimelineStepConfig,
) {
  if (steps.some((item) => item.id === step.id)) return steps;
  const anchorIndex = steps.findIndex((item) => anchorIds.includes(item.id));
  if (anchorIndex < 0) return [...steps, step];
  return [
    ...steps.slice(0, anchorIndex + 1),
    step,
    ...steps.slice(anchorIndex + 1),
  ];
}

function buildCycleTimelineSteps(
  steps: TreatmentStepConfig[],
  cycleType: string,
  t: TranslationFunction,
) {
  let timelineSteps: CycleChartTimelineStepConfig[] = steps.map((step) => ({
    id: step.id,
    title: step.title,
    shortTitle: step.shortTitle,
  }));

  if (OVARIAN_CYCLE_TYPES.has(cycleType)) {
    timelineSteps = insertTimelineStepAfter(
      timelineSteps,
      ["protocol", "stimulation"],
      {
        id: "trigger_shot",
        title: t("care.cycle_detail.timeline_steps.trigger_shot_full"),
        shortTitle: t("care.cycle_detail.timeline_steps.trigger"),
      },
    );
  }

  if (EMBRYO_LAB_CYCLE_TYPES.has(cycleType)) {
    timelineSteps = insertTimelineStepAfter(timelineSteps, ["egg_retrieval"], {
      id: "sperm_collection",
      title: t("care.cycle_detail.timeline_steps.sperm_sample"),
      shortTitle: t("care.cycle_detail.timeline_steps.sperm_short"),
    });
    timelineSteps = insertTimelineStepAfter(timelineSteps, ["fertilization"], {
      id: "embryo_development",
      title: t("care.cycle_detail.timeline_steps.embryo_culture"),
      shortTitle: t("care.cycle_detail.timeline_steps.embryo_culture"),
    });
  }

  return timelineSteps;
}

// ─── SCREEN ──────────────────────────────────────────────────

export default function CycleDetailScreen({ navigation, route }: Props) {
  const { openDocumentUpload, pickAndOpenDocumentUpload } =
    useDocumentUploadController();
  const insets = useSafeAreaInsets();
  const t = useTranslations();
  const capturePress = usePressCapture("CycleDetailScreen");
  const devCryoBridgeFixture = isDevCryoBridgeFixture(route.params?.cycleId);
  const { locale } = useLocale();
  const handleBack = useSmartBack({
    fallbackTab: "Care",
    fallbackScreen: "CareOverview",
  });
  const initialPhaseId = route.params?.initialPhaseId;
  useEffect(() => {
    if (initialPhaseId) {
      // Phase deep-link entry point. The screen renders the active cycle's
      // current phase by default; explicit per-phase scroll is a follow-up.
    }
  }, [initialPhaseId]);

  // ── Local UI state ──
  const [calendarMedicationSheet, setCalendarMedicationSheet] =
    useState<HomepageMedicationFactSheetPayload | null>(null);
  const [calendarAppointmentSheet, setCalendarAppointmentSheet] =
    useState<AppointmentDetailItem | null>(null);
  const [editAppointment, setEditAppointment] =
    useState<AppointmentDetailItem | null>(null);
  const [calendarProcedureSheet, setCalendarProcedureSheet] =
    useState<HomepageProcedureFactSheetPayload | null>(null);
  const [calendarProcedureEducationOnly, setCalendarProcedureEducationOnly] =
    useState(false);
  const [calendarSymptomOverlay, setCalendarSymptomOverlay] =
    useState<CalendarSymptomOverlayState | null>(null);
  /**
   * Shown when she dates an upcoming collection of her partner's sample and no
   * partner account is linked. The appointment is still hers; this is the offer
   * to put it in his calendar too, which needs him connected.
   */
  const [partnerCalendarPromptVisible, setPartnerCalendarPromptVisible] =
    useState(false);
  const [careTeamModalVisible, setCareTeamModalVisible] = useState(() => {
    if (!__DEV__ || typeof window === "undefined") return false;
    return (
      new URLSearchParams(window.location.search).get(
        "dev_open_cycle_care_team",
      ) === "1"
    );
  });

  const handleOpenCalendarProcedure = useCallback(
    (payload: HomepageProcedureFactSheetPayload) => {
      setCalendarProcedureEducationOnly(false);
      setCalendarProcedureSheet(payload);
    },
    [],
  );
  const handleOpenCalendarAppointmentEducation = useCallback(
    (appointment: AppointmentDetailItem) => {
      const item = getProcedureByName(appointment.title);
      if (!item) return;

      setCalendarAppointmentSheet(null);
      setCalendarProcedureEducationOnly(true);
      setCalendarProcedureSheet({
        item,
        appointment: {
          id: appointment.id,
          appointmentId: appointment.appointmentId,
          title: appointment.title,
          subtitle: appointment.subtitle,
          time: appointment.time,
          date: appointment.date,
          notes: appointment.notes ?? undefined,
          ownership: appointment.ownership,
          ownerId: appointment.ownerId,
        },
      });
    },
    [],
  );
  useCalendarLoggingTabBarOverride(calendarSymptomOverlay);
  // ── Real data hooks ──
  const { data: patient } = usePatient();
  const isPartnerViewer = isCurrentPartnerCareViewer(patient);
  const cyclePatientId = isPartnerViewer ? patient?.partnerId : undefined;
  const viewerCycle = useActiveCycleWithPhases({
    cycleId: route.params?.cycleId,
  });
  const linkedPartnerCycle = useActiveCycleWithPhases({
    cycleId: route.params?.cycleId,
    patientId: cyclePatientId,
  });
  const isPartnerCycle =
    isPartnerViewer &&
    shouldUsePartnerCyclePerspective({
      patient,
      cycleType: linkedPartnerCycle.cycle?.cycleType,
    });
  const activeCycle = isPartnerCycle ? linkedPartnerCycle : viewerCycle;
  const resolveCycleNavigation = useCycleNavigation();
  const { data: viewerCycles } = useCycles();
  const { data: linkedPartnerCycles } = useCycles({
    patientId: cyclePatientId,
  });
  const allCycles = isPartnerCycle ? linkedPartnerCycles : viewerCycles;
  const { tenantId, patientId } = useApiClient();
  const { data: hubPhases = [] } = usePhases(activeCycle.cycle?.id);
  const { mutateAsync: createPhase } = useCreatePhase();
  const { mutateAsync: updatePhase } = useUpdatePhase();
  const updateCycleMutation = useUpdateCycle();
  const completeCycle = useCompleteCycle();
  const createSnapshot = useCreateTreatmentSnapshot();
  const recordCycleOutcome = useRecordCycleOutcome();
  const createBetaTestResult = useCreateTestResult();
  const updateBetaTestResult = useUpdateTestResult();
  const createFETBetaOrder = useCreateFETBetaOrder();
  const recordFETBetaResult = useRecordFETBetaResult();
  const reportFETTransferSelf = useReportFETTransferSelf();
  const updateFETProcedureReport = useUpdateFETProcedureReport();
  const createAppointment = useCreateAppointment();
  const updateAppointment = useUpdateAppointment();
  const deleteAppointment = useDeleteAppointment();
  const queryClient = useQueryClient();
  const { data: partnerPatient } = usePatient({
    patientId: cyclePatientId ?? null,
  });
  /**
   * The typed test result this recording created, and the value it holds. The
   * value is retained with the id because a failed outcome write leaves the
   * modal editable: a retry with a corrected number has to re-stamp that row
   * rather than link a row that still says the old one.
   */
  const [betaTestResult, setBetaTestResult] = useState<{
    id: string;
    value: number;
  } | null>(null);
  const fetBetaOrderRequestIdRef = useRef<string | null>(null);
  const fetBetaResultRequestIdRef = useRef<string | null>(null);
  const fetProcedureReportRequestIdRef = useRef<string | null>(null);
  const betaOutcomeRequestIdRef = useRef<OutcomeWriteRequestId | null>(null);
  // Handle used to push a "scheduled" optimistic state into the treatment hub
  // the moment `AddAppointmentModal` saves a procedure. Without it the hub
  // waits on the phase refetch and the "Planifier …" schedule row keeps
  // rendering until the round-trip completes.
  const treatmentHubRef = useRef<MoonTreatmentHubCardHandle | null>(null);

  // ── Derive cycle info ──
  const cycle = activeCycle.cycle;
  const cycleType = (cycle?.cycleType ?? "ivf") as CycleType;
  const isMonitoredCycle = isMonitoredCycleType(cycle?.cycleType);
  // Declared here rather than beside the other cycle queries because the hub's
  // step progress is derived from it: an FET transfer lives in this ledger, not
  // in a phase.
  const fetBetaLifecycleQuery = useFETBetaLifecycle({
    cycleId: cycle?.id ?? null,
    enabled: cycleType === "fet" && !isPartnerCycle,
  });

  const fetPlanForCycleQuery = useFETPlanForCycle({
    cycleId: cycle?.id ?? null,
    enabled: cycleType === "fet" && !isPartnerCycle,
  });
  const embryoFreezingPlanId =
    !isPartnerCycle && cycle?.cycleType === "embryo_freezing"
      ? (cycle.embryoFreezingPlanSnapshot?.planId ?? null)
      : null;
  const { data: embryoFreezingPlan } =
    useEmbryoFreezingPlan(embryoFreezingPlanId);
  const pgtPlan = useMemo(
    () =>
      embryoFreezingPlan?.versions.find(
        (version) => version.version === embryoFreezingPlan.currentVersion,
      )?.pgtPlan ?? null,
    [embryoFreezingPlan],
  );
  const cycleTypeKey = `care.cycle_type.${cycleType}`;
  const displayName =
    t(cycleTypeKey) !== cycleTypeKey
      ? t(cycleTypeKey)
      : (CYCLE_TYPE_LABELS[cycleType] ?? cycleType);
  const { data: documents } = useDocuments();
  const { data: cycleCareTeamMembers } = useCareTeam(cycle?.id);

  const cycleNumber = useMemo(() => {
    if (!cycle) return 1;
    const sameType = (allCycles ?? []).filter(
      (c) => c.cycleType === cycle.cycleType,
    );
    const idx = sameType.findIndex((c) => c.id === cycle.id);
    return idx >= 0 ? idx + 1 : 1;
  }, [cycle, allCycles]);

  // Most recently-closed cycle with a recorded outcome — used to render an
  // outcome-aware close-out card on this screen's empty state, so a patient
  // who just finished a cycle doesn't land on the bare first-time copy.
  // Sort by updatedAt: cycles closed in the same session share an endDate,
  // so we want the one whose outcome was saved last.
  const lastClosedOutcome = useMemo(() => {
    const closed = (allCycles ?? []).filter((c) => c.outcome?.type);
    if (closed.length === 0) return null;
    const sorted = [...closed].sort((a, b) =>
      (b.updatedAt ?? "").localeCompare(a.updatedAt ?? ""),
    );
    return sorted[0]?.outcome?.type ?? null;
  }, [allCycles]);

  const phases = activeCycle.phases ?? [];
  const currentPhase = activeCycle.currentPhase;

  const perspective: StepPerspective = isPartnerCycle ? "partner" : "self";
  const partnerDisplayName =
    perspective === "partner"
      ? partnerPatient?.firstName || partnerPatient?.preferredName || undefined
      : undefined;

  const { data: linkedSpecimens } = useSpecimens();
  const frozenCycleSelection = useMemo(
    () =>
      getFrozenSpecimenSelectionForCycle(linkedSpecimens, cycle?.id ?? null),
    [cycle?.id, linkedSpecimens],
  );
  const usingFrozenSpecimens = frozenCycleSelection.usingFrozenSpecimens;

  const steps = useMemo(() => {
    const base = getStepsForCycle(cycleType, perspective, t, {
      protocol: cycle?.protocol,
      phases: hubPhases,
      pgtPlan,
      cyclePlanSnapshot: cycle?.cyclePlanSnapshot,
    });
    if (
      !usingFrozenSpecimens ||
      (cycleType !== "ivf" && cycleType !== "icsi")
    ) {
      return base;
    }
    return base
      .filter((s) => s.id !== "egg_retrieval")
      .map((s) =>
        s.id === "protocol"
          ? {
              ...s,
              title: t("care.cycle_detail.frozen_protocol.step_title"),
              description: t("care.cycle_detail.frozen_protocol.description"),
              prompt: t("care.cycle_detail.frozen_protocol.prompt"),
              skipText: t("care.cycle_detail.frozen_protocol.skip_text"),
              hasTriggerShot: false,
            }
          : s,
      );
  }, [
    cycle?.protocol,
    cycle?.cyclePlanSnapshot,
    cycleType,
    hubPhases,
    pgtPlan,
    perspective,
    usingFrozenSpecimens,
    t,
  ]);

  const derivedStepProgress = useMemo((): TreatmentStepProgress[] => {
    if (!cycle) return [];
    const base = deriveStepProgress(hubPhases, steps).map((p) => {
      const stepData = { ...p.data };
      const cycleOutcomeNotes = cycle?.outcome?.notes;
      if (p.stepId === "freezing" && cycleOutcomeNotes) {
        stepData.notes = cycleOutcomeNotes;
      }
      return {
        stepId: p.stepId,
        phaseId: p.phaseId,
        status: p.status,
        completedAt: p.completedAt,
        medications: [],
        data: stepData,
        notes: null,
      };
    });
    // An FET transfer is recorded in the FET procedure ledger, not as a phase,
    // so the phase-derived status above reads "pending" for a transfer she has
    // already confirmed — the step kept offering its entry form with the embryo
    // count nowhere in sight. Derived rather than written: the procedure holds
    // the date and the count already, and writing them onto a phase as well
    // would let the two records disagree. Reading the ledger also repairs every
    // cycle confirmed before this existed, which a write at confirmation time
    // could not.
    const fetTransfer =
      cycleType === "fet" ? fetBetaLifecycleQuery.data?.procedure : null;
    // Only a status that actually says the transfer was reported counts. Reading
    // "anything not cancelled" showed a `scheduled` or `rescheduled` row as a
    // confirmed transfer — green, dated, with an embryo count — while
    // `isFETWaitEligible` and the backend's beta-order gate both refused it. She
    // would have seen a confirmed transfer with no way to record a beta and no
    // way to end the cycle.
    const withFET = isReportedFETTransfer(fetTransfer)
      ? base.map((sp) =>
          sp.stepId === "transfer"
            ? {
                ...sp,
                status: "completed" as const,
                completedAt: fetTransfer.actualDate,
                data: {
                  ...sp.data,
                  // Same shape `deriveStepProgress` writes for a phase-backed
                  // step, seconds included — the form and the save path both
                  // read this field back.
                  transferDate: fetTransfer.actualTime
                    ? `${fetTransfer.actualDate}T${fetTransfer.actualTime}:00`
                    : fetTransfer.actualDate,
                  ...(fetTransfer.actualTransferCount != null
                    ? { embryosTransferred: fetTransfer.actualTransferCount }
                    : {}),
                  ...(fetTransfer.embryoType
                    ? { embryoType: fetTransfer.embryoType }
                    : {}),
                  ...(fetTransfer.notes ? { notes: fetTransfer.notes } : {}),
                },
              }
            : sp,
        )
      : base;
    // Dev-only: force specific panel states so completed/skipped/scheduled can
    // be reviewed (see `?dev_stepStates=`).
    const forcedStates = DEV_STEP_STATES_OVERRIDE;
    if (!forcedStates) return withFET;
    const todayStr = toLocalDateString(new Date());
    const soon = new Date();
    soon.setDate(soon.getDate() + 3);
    const soonStr = toLocalDateString(soon);
    return withFET.map((sp) => {
      const forced = forcedStates[sp.stepId];
      if (!forced) return sp;
      if (forced === "completed") {
        return { ...sp, status: "completed" as const, completedAt: todayStr };
      }
      if (forced === "skipped") {
        return { ...sp, status: "skipped" as const };
      }
      if (forced === "outcome") {
        return {
          ...sp,
          status: "pending" as const,
          data: {
            ...sp.data,
            procedureDate: todayStr,
            eggsCollected: 12,
          },
        };
      }
      // scheduled: future procedure/transfer date drives MoonScheduledStepView
      return {
        ...sp,
        status: "scheduled" as const,
        scheduledFor: soonStr,
        data: {
          ...sp.data,
          procedureDate: `${soonStr}T09:00:00`,
          transferDate: `${soonStr}T09:00:00`,
        },
      };
    });
  }, [
    cycle,
    cycleType,
    hubPhases,
    steps,
    fetBetaLifecycleQuery.data?.procedure,
  ]);

  /**
   * Steps whose record belongs to the clinic rather than to her.
   *
   * The FET transfer is the one step read out of a ledger a clinic writes to as
   * well, and a row it authored is not hers to correct: the save below refuses
   * to PATCH over it, and the self-report endpoint answers a replay by returning
   * the clinic's own values. Until this, that refusal was silent — she edited,
   * confirmed, and the centre's figures came straight back as if she had typed
   * them. The hub now locks the step and says where the values came from.
   *
   * Only when the clinic has the hand. A transfer she reported herself stays
   * editable and carries no provenance line.
   *
   * The limit this makes visible rather than fixes: there is no patient-side way
   * to correct a clinic's transfer at all. That is deliberate — her app is not
   * the system of record for what a centre performed — so the answer is a
   * sentence pointing her at her care team, not an edit that would need a
   * backend able to accept a patient amendment to a provider row.
   */
  const clinicRecordedStepIds = useMemo(() => {
    const procedure =
      cycleType === "fet" ? fetBetaLifecycleQuery.data?.procedure : null;
    // `isReportedFETTransfer` is the same test that decides the step is
    // complete. It has to be: the lock renders inside the completed-step panel,
    // so flagging a row this call would not complete produced no lock at all.
    const isClinicsRecord =
      isReportedFETTransfer(procedure) &&
      !isPatientOwnedFETProcedure(procedure);
    return isClinicsRecord ? ["transfer"] : [];
  }, [cycleType, fetBetaLifecycleQuery.data?.procedure]);

  const currentStepId = useMemo(() => {
    if (devCryoBridgeFixture) return null;
    for (const step of steps) {
      const sp = derivedStepProgress.find((p) => p.stepId === step.id);
      if (!sp || (sp.status !== "completed" && sp.status !== "skipped")) {
        return step.id;
      }
    }
    return null;
  }, [devCryoBridgeFixture, steps, derivedStepProgress]);

  const hubCycle = useMemo((): HubCycle | null => {
    if (!cycle) return null;
    return {
      id: cycle.id,
      cycleType: cycle.cycleType,
      startDate: cycle.startDate ?? null,
      clinic: cycle.provider ?? null,
      stepProgress: derivedStepProgress,
      currentPhase: currentPhase?.type as
        | import("@louise/care/treatment-phases").TreatmentPhaseName
        | null
        | undefined,
      phaseStartDate: currentPhase?.startDate ?? null,
      pgtPlan,
      cyclePlanSnapshot: cycle.cyclePlanSnapshot,
    };
  }, [cycle, currentPhase, derivedStepProgress, pgtPlan]);

  const treatmentGoal = useMemo((): "pregnancy" | "preserve" => {
    const ct = cycle?.cycleType;
    if (
      ct === "egg_freezing" ||
      ct === "embryo_freezing" ||
      ct === "sperm_freezing"
    ) {
      return "preserve";
    }
    return "pregnancy";
  }, [cycle]);

  const specimenPrefill = useMemo((): SpecimenPrefill | undefined => {
    if (treatmentGoal !== "preserve") return undefined;
    const getStepData = (stepId: string): Record<string, unknown> =>
      (derivedStepProgress.find((p) => p.stepId === stepId)?.data ??
        {}) as Record<string, unknown>;

    const retrieval = getStepData("egg_retrieval");
    const freezing = getStepData("freezing");
    const fertilization = getStepData("fertilization");
    const collection = getStepData("sperm_collection");
    const clinic = cycle?.provider ?? undefined;
    const isEmbryoFreezing = cycle?.cycleType === "embryo_freezing";
    const isSpermFreezing = cycle?.cycleType === "sperm_freezing";

    if (isSpermFreezing) {
      const analysis = getStepData("analysis");
      // sperm_collection's date round-trips as `procedureDate` (the generic
      // date field name used by deriveStepProgress) but the form-side field
      // name is `collectionDate`. Read either.
      const rawCollectionDate =
        (collection.collectionDate as string | undefined) ??
        (collection.procedureDate as string | undefined);
      return {
        type: "sperm",
        clinicName: clinic || "",
        collectedAt: rawCollectionDate ?? "",
        freezeDate: (freezing.freezeDate as string | undefined) ?? "",
        numberFrozen: (freezing.vialsFrozen as number) ?? undefined,
        abstinencePeriod: (collection.abstinenceDays as number) ?? undefined,
        // Pull semen analysis values from the analysis step so the user can
        // confirm/edit them on the specimen form.
        concentration: (analysis.concentration as number) ?? undefined,
        totalCount: (analysis.totalCount as number) ?? undefined,
        motilityPercent: (analysis.motilityPercent as number) ?? undefined,
        morphologyPercent: (analysis.morphologyPercent as number) ?? undefined,
        volumeMl: (analysis.volumeMl as number) ?? undefined,
      };
    }

    // For eggs the freezing step writes its date as `procedureDate` (the
    // generic date field name used by `deriveStepProgress`). Reading it here
    // lets the cryo-bridge prefill the freeze date the same way embryos do.
    const eggFreezeDate =
      (freezing.freezeDate as string | undefined) ??
      (freezing.procedureDate as string | undefined);
    return {
      type: isEmbryoFreezing ? "embryos" : "eggs",
      clinicName: clinic || (fertilization.storageLocation as string) || "",
      retrievalDate: (retrieval.procedureDate as string | undefined) ?? "",
      freezeDate:
        (isEmbryoFreezing
          ? (fertilization.procedureDate as string | undefined)
          : eggFreezeDate) ?? "",
      totalRetrieved: (retrieval.eggsCollected as number) ?? undefined,
      matureMII: (freezing.matureEggs as number) ?? undefined,
      numberFrozen: isEmbryoFreezing
        ? ((fertilization.embryosFrozen as number) ?? undefined)
        : ((freezing.eggsFrozen as number) ?? undefined),
      fertilizationMethod:
        (fertilization.fertilizationMethod as string) ?? undefined,
      embryoGrade: (fertilization.embryoGrade as string) ?? undefined,
      storageLocation: (fertilization.storageLocation as string) ?? undefined,
    };
  }, [treatmentGoal, derivedStepProgress, cycle]);

  // ── TWW derivation ──
  // The cycle is in the two-week-wait surface when:
  //   - it's a transfer-bearing cycle (treatmentGoal === "pregnancy")
  //   - status is open (not closed)
  //   - the luteal_phase row exists with a startDate ≤ today
  //   - no outcome.type has been recorded yet
  const lutealPhase = useMemo(
    () => hubPhases.find((p) => p.type === "luteal_phase") ?? null,
    [hubPhases],
  );

  const cycleOutcome = useMemo(() => cycle?.outcome ?? null, [cycle]);

  // Confetti over a recorded loss is the one thing this surface must never do,
  // so the rule lives in a presenter where it can be asserted.
  const mayCelebrateCompletion = useMemo(
    () => completionMayBeCelebrated(cycleOutcome),
    [cycleOutcome],
  );

  /**
   * Whether the treatment is behind her, however much of it she logged.
   *
   * Deliberately not `isInWaitingPhase`: that needs a `luteal_phase` row, which
   * only exists once someone logs luteal support or sets a test date. A patient
   * who did neither is in the two-week wait as far as her body is concerned and
   * invisible to the app — the same gap that left her with no way to record a
   * result. The procedure having happened is the fact the app actually holds.
   */
  const treatmentIsDone = useMemo(() => {
    if (treatmentGoal !== "pregnancy") return false;
    if (cycleOutcome?.type) return true;
    const today = toLocalDateString(new Date());
    return !!findLatestPerformedProcedurePhase(
      hubPhases.filter((phase) => phase.startDate && phase.startDate <= today),
      ["transfer", "insemination"],
    )?.startDate;
  }, [treatmentGoal, cycleOutcome?.type, hubPhases]);

  const hasConfirmedTransfer = useMemo(
    () => hasConfirmedPregnancyProcedure(hubPhases),
    [hubPhases],
  );

  const isInWaitingPhase = useMemo(() => {
    if (treatmentGoal !== "pregnancy") return false;
    if (!cycle || cycle.status !== "open") return false;
    if (cycleType === "fet") {
      return (
        !cycleOutcome?.type &&
        isFETWaitEligible(
          fetBetaLifecycleQuery.data?.procedure,
          selectLatestFETBetaOrder(fetBetaLifecycleQuery.data),
        )
      );
    }
    const today = toLocalDateString(new Date());
    const performedProcedure = findLatestPerformedProcedurePhase(
      hubPhases.filter((phase) => phase.startDate && phase.startDate <= today),
      ["transfer", "insemination"],
    );
    if (!hasConfirmedTransfer || !performedProcedure?.startDate) return false;
    if (!lutealPhase?.startDate) return false;
    if (lutealPhase.startDate < performedProcedure.startDate) return false;
    const startMs = new Date(lutealPhase.startDate + "T00:00:00").getTime();
    if (lutealPhase.endDate) {
      const endMs = new Date(lutealPhase.endDate + "T00:00:00").getTime();
      const windowDays = Math.round((endMs - startMs) / (1000 * 60 * 60 * 24));
      if (windowDays < 7) return false;
    }
    if (lutealPhase.startDate > today) return false;
    if (cycleOutcome?.type) return false;
    return true;
  }, [
    treatmentGoal,
    cycle,
    cycleType,
    cycleOutcome?.type,
    fetBetaLifecycleQuery.data,
    hubPhases,
    hasConfirmedTransfer,
    lutealPhase,
  ]);

  /**
   * A result is recorded but the cycle is still open — the state that exists
   * because recording no longer closes the cycle.
   *
   * The patient is still on the outcome step here, so this has to keep the hub
   * there. `isInWaitingPhase` goes false the moment an outcome type exists, and
   * the hub sits on the furthest step marked "active", falling back to the first
   * unfinished one — which walks the pointer *backwards* onto an earlier step,
   * away from the result that was just recorded.
   */
  const hasRecordedOpenResult = useMemo(
    () => cycle?.status === "open" && !!cycleOutcome?.type,
    [cycle?.status, cycleOutcome?.type],
  );

  /** The outcome step is where the patient stands for both of those states. */
  const isOnOutcomeStep = isInWaitingPhase || hasRecordedOpenResult;

  const waitingFETOrder =
    cycleType === "fet"
      ? selectLatestFETBetaOrder(fetBetaLifecycleQuery.data)
      : null;
  const waitingStartDate =
    cycleType === "fet"
      ? (fetBetaLifecycleQuery.data?.procedure.actualDate ?? null)
      : (lutealPhase?.startDate ?? null);
  const waitingTestDate =
    cycleType === "fet"
      ? getFETBetaOrderDate(waitingFETOrder)
      : (lutealPhase?.endDate ?? null);

  // ── Treatment Hub state ──
  const [addMedVisible, setAddMedVisible] = useState(false);
  const [hubMedCycleId, setHubMedCycleId] = useState<string | null>(null);
  const [hubMedPhaseId, setHubMedPhaseId] = useState<string | null>(null);
  const [hubMedRole, setHubMedRole] = useState<
    "standard" | "trigger_shot" | null
  >(null);
  const [editMed, setEditMed] = useState<EditMedicationData | null>(null);
  const [startTreatmentVisible, setStartTreatmentVisible] = useState(false);
  const [addPastCycleVisible, setAddPastCycleVisible] = useState(false);
  const [addAppointmentVisible, setAddAppointmentVisible] = useState(false);
  // Seeds AddAppointmentModal for a "create" flow launched pre-filled — used by
  // the two-week-wait card's pregnancy-test row to schedule a beta hCG test.
  const [appointmentPrefill, setAppointmentPrefill] = useState<NonNullable<
    React.ComponentProps<typeof AddAppointmentModal>["prefill"]
  > | null>(null);
  const [editTreatmentData, setEditTreatmentData] =
    useState<EditTreatmentData | null>(null);
  const [editTreatmentInitialStep, setEditTreatmentInitialStep] = useState<
    1 | 2
  >(1);
  const [
    allowTreatmentTypeBackNavigation,
    setAllowTreatmentTypeBackNavigation,
  ] = useState(true);
  const [endCycleVisible, setEndCycleVisible] = useState(false);
  const [endCycleMode, setEndCycleMode] = useState<"end" | "complete">("end");
  const [addSpecimenVisible, setAddSpecimenVisible] = useState(false);
  const [addSpecimenForRetrieval, setAddSpecimenForRetrieval] = useState(false);
  const [selectedSpecimenId, setSelectedSpecimenId] = useState<string | null>(
    null,
  );
  const [betaResultVisible, setBetaResultVisible] = useState(false);
  const [betaResultError, setBetaResultError] = useState<string | null>(null);
  const [updatePregnancyVisible, setUpdatePregnancyVisible] = useState(false);
  const [updatePregnancyError, setUpdatePregnancyError] = useState<
    string | null
  >(null);
  const updatePregnancyRequestIdRef = useRef<OutcomeWriteRequestId | null>(
    null,
  );
  const [isSavingTestDate, setIsSavingTestDate] = useState(false);
  const [testDateSaveError, setTestDateSaveError] = useState<string | null>(
    null,
  );
  const [pendingTestDate, setPendingTestDate] = useState<string | null>(null);
  useEffect(() => {
    if (!__DEV__ || typeof window === "undefined" || !isInWaitingPhase) {
      return;
    }
    if (
      new URLSearchParams(window.location.search).get(
        "dev_open_beta_result",
      ) === "1"
    ) {
      setBetaResultVisible(true);
    }
  }, [isInWaitingPhase]);
  const createCycle = useCreateCycle();
  const createEmbryoFreezingPlan = useCreateEmbryoFreezingPlan();
  const acceptEmbryoFreezingPlan = useAcceptEmbryoFreezingPlan();
  const {
    cycleTotalEggs: totalEggs,
    cycleTotalEmbryos: totalEmbryos,
    cycleTotalSperm: totalSperm,
    cycleSpecimens,
    cycleStorageGroups: storageGroups,
  } = useFrozenSpecimensForCycle(cycle?.id);
  const { data: oocyteCohorts = [] } = useOocyteCohorts();

  const preservationOutcomeCounts = useMemo<PreservationOutcomeCounts>(() => {
    const dataFor = (stepId: string) =>
      derivedStepProgress.find((progress) => progress.stepId === stepId)
        ?.data ?? {};
    const retrieval = dataFor("egg_retrieval");
    const freezing = dataFor("freezing");
    const fertilization = dataFor("fertilization");
    const numberOrNull = (value: unknown): number | null =>
      typeof value === "number" && Number.isFinite(value) ? value : null;

    if (cycle?.cycleType === "sperm_freezing") {
      return {
        kind: "sperm",
        retrieved: null,
        mature: null,
        fertilized: null,
        frozen: numberOrNull(freezing.vialsFrozen),
      };
    }

    return {
      kind: cycle?.cycleType === "embryo_freezing" ? "embryos" : "eggs",
      retrieved: numberOrNull(retrieval.eggsCollected),
      mature: numberOrNull(freezing.matureEggs),
      fertilized:
        cycle?.cycleType === "embryo_freezing"
          ? numberOrNull(fertilization.eggsFertilized)
          : null,
      frozen:
        cycle?.cycleType === "embryo_freezing"
          ? numberOrNull(
              fertilization.embryosFrozen ?? fertilization.eggsFrozen,
            )
          : numberOrNull(freezing.eggsFrozen),
    };
  }, [cycle?.cycleType, derivedStepProgress]);
  const hasRecordedPreservationOutcome =
    (cycle?.cycleType === "egg_freezing" ||
      cycle?.cycleType === "embryo_freezing" ||
      cycle?.cycleType === "sperm_freezing") &&
    preservationOutcomeCounts.frozen != null;
  const cryoSpecimenDetails = useMemo(
    () =>
      devCryoBridgeFixture
        ? DEV_CRYO_BRIDGE_SPECIMENS
        : mapApiSpecimensToCryoDetails(cycleSpecimens, (apiType, index) =>
            buildCryoSpecimenLabel(t, apiType, index),
          ),
    [cycleSpecimens, devCryoBridgeFixture, t],
  );
  const bridgeSpecimens = useMemo<CryoSpecimenSnapshot[]>(
    () =>
      devCryoBridgeFixture
        ? DEV_CRYO_BRIDGE_SNAPSHOTS
        : buildCryoSpecimenSnapshotsFromStorageGroups(
            t,
            locale,
            storageGroups,
            oocyteCohorts,
          ),
    [devCryoBridgeFixture, locale, oocyteCohorts, storageGroups, t],
  );

  const handleEditTreatment = useCallback(() => {
    if (!cycle) return;
    setEditTreatmentData({
      cycleType: cycle.cycleType as CycleType,
      clinicName: cycle.provider ?? "",
      startDate: cycle.startDate
        ? new Date(cycle.startDate + "T00:00:00")
        : null,
    });
    setEditTreatmentInitialStep(1);
    setAllowTreatmentTypeBackNavigation(true);
    setStartTreatmentVisible(true);
  }, [cycle]);
  const handleEditTreatmentWhereWhen = useCallback(() => {
    if (!cycle) return;
    setEditTreatmentData({
      cycleType: cycle.cycleType as CycleType,
      clinicName: cycle.provider ?? "",
      startDate: cycle.startDate
        ? new Date(cycle.startDate + "T00:00:00")
        : null,
    });
    setEditTreatmentInitialStep(2);
    setAllowTreatmentTypeBackNavigation(false);
    setStartTreatmentVisible(true);
  }, [cycle]);
  const handleOpenStartTreatment = useCallback(() => {
    setEditTreatmentData(null);
    setEditTreatmentInitialStep(1);
    setAllowTreatmentTypeBackNavigation(true);
    setStartTreatmentVisible(true);
  }, []);

  const heroMenuItems = useMemo<KebabMenuItem[]>(() => {
    const items: KebabMenuItem[] = [
      {
        id: "edit",
        label: t("care.cycle_detail.menu.edit_cycle"),
        icon: Pencil,
        onPress: handleEditTreatment,
      },
    ];
    const isPreservation =
      cycle?.cycleType === "egg_freezing" ||
      cycle?.cycleType === "embryo_freezing" ||
      cycle?.cycleType === "sperm_freezing";
    if (isPreservation) {
      items.push({
        id: "add-specimen",
        label: t("care.cycle_detail.menu.add_specimen"),
        icon: Plus,
        onPress: () => setAddSpecimenVisible(true),
      });
    }
    items.push({
      id: "end",
      label: t("care.cycle_detail.menu.end_cycle"),
      icon: XCircle,
      tone: "destructive",
      onPress: () => {
        setEndCycleMode("end");
        setEndCycleVisible(true);
      },
    });
    return items;
  }, [cycle?.cycleType, handleEditTreatment, t]);

  const handleStartTreatment = useCallback(
    async (formData: StartTreatmentFormData) => {
      const startDateStr = formData.startDate
        ? formData.startDate.toISOString().split("T")[0]
        : null;

      const cycleType = formData.treatmentType;

      try {
        if (editTreatmentData && cycle?.id) {
          await updateCycleMutation.mutateAsync({
            cycleId: cycle.id,
            data: {
              cycleType,
              provider: formData.clinicName || null,
              startDate: startDateStr,
            },
          });
        } else {
          let embryoFreezingPlan: Awaited<
            ReturnType<typeof createEmbryoFreezingPlan.mutateAsync>
          > | null = null;
          if (cycleType === "embryo_freezing") {
            const draftPlan = await createEmbryoFreezingPlan.mutateAsync(
              createMinimalEmbryoFreezingPlanInput(createClientRequestId()),
            );
            embryoFreezingPlan = await acceptEmbryoFreezingPlan.mutateAsync({
              planId: draftPlan.id,
              input: {
                clientRequestId: createClientRequestId(),
                expectedVersion: draftPlan.currentVersion,
              },
            });
          }
          await createCycle.mutateAsync({
            cycleType,
            provider: formData.clinicName || null,
            startDate: startDateStr,
            status: "open",
            ...(embryoFreezingPlan
              ? {
                  embryoFreezingPlanSnapshot: {
                    planId: embryoFreezingPlan.id,
                    expectedVersion: embryoFreezingPlan.currentVersion,
                    clientRequestId: createClientRequestId(),
                  },
                }
              : {}),
          });
        }
        await queryClient.refetchQueries({
          queryKey: ["active-cycle"],
          type: "all",
        });
        await queryClient.refetchQueries({
          queryKey: ["cycle-phases"],
          type: "all",
        });
        setEditTreatmentData(null);
        setEditTreatmentInitialStep(1);
        setAllowTreatmentTypeBackNavigation(true);
        setStartTreatmentVisible(false);
      } catch (e) {
        console.error("[CycleDetail] Failed to save cycle:", e);
        Alert.alert(
          t("care.cycle_detail.alerts.save_failed_title"),
          t("care.cycle_detail.alerts.save_failed_message"),
          [{ text: "OK" }],
        );
      }
    },
    [
      createCycle,
      createEmbryoFreezingPlan,
      acceptEmbryoFreezingPlan,
      updateCycleMutation,
      queryClient,
      editTreatmentData,
      cycle,
      t,
    ],
  );

  const handleOpenMedicationModal = useCallback(
    (opts: {
      cycleId: string;
      phaseId?: string | null;
      medicationRole?: "standard" | "trigger_shot" | null;
    }) => {
      setHubMedCycleId(opts.cycleId);
      setHubMedPhaseId(opts.phaseId ?? null);
      setHubMedRole(opts.medicationRole ?? null);
      setAddMedVisible(true);
    },
    [],
  );

  const handleUpdateStepProgress = useCallback(
    async (
      stepId: string,
      data: Partial<TreatmentStepProgress>,
      options?: {
        /**
         * Set by the appointment flow, which has just written the appointment
         * itself and is only mirroring its date onto the phase.
         *
         * Without it the sync below saw the `appointmentId` that mirror carries,
         * resolved to `update`, and PATCHed the appointment one tick after
         * `AddAppointmentModal` saved it — with a payload rebuilt from the step's
         * canonical scheduling entry. Her title became "Egg retrieval", a
         * multi-activity visit lost every activity but one, and the duration she
         * chose collapsed to the 60-minute default. The step modal still syncs;
         * only the path that is echoing an appointment opts out.
         */
        skipCalendarSync?: boolean;
      },
    ) => {
      if (!cycle?.id) return;

      const phaseType = getPhaseTypeForStep(stepId);
      if (!phaseType) {
        throw new Error(
          `[CycleDetailScreen] Cannot save ${stepId}: no API phase mapping exists.`,
        );
      }
      try {
        const now = new Date();
        const today = `${now.getFullYear()}-${(now.getMonth() + 1).toString().padStart(2, "0")}-${now.getDate().toString().padStart(2, "0")}`;
        const latestPhases =
          queryClient.getQueryData<ApiPhase[]>(
            phaseKeys.cycle(tenantId ?? "", cycle.id),
          ) ?? hubPhases;
        const existingPhase =
          latestPhases.find((p) => p.type === phaseType) ?? null;

        // A step that borrows another step's phase may read it, not summon it.
        // Saving `outcome` with no luteal phase yet would create one dated
        // today, and a luteal phase that predates the transfer permanently
        // fails the `isInWaitingPhase` guard below — the two-week wait would
        // never appear for this cycle again. The panel no longer offers the
        // action; this is the half that does not rely on it.
        if (!existingPhase && !stepMayCreatePhase(stepId)) return;

        const stepData = data.data ?? {};

        /**
         * Put an upcoming procedure in her calendar, the way the scheduling row
         * already does.
         *
         * The hub told her, on the very card that collects this date, that
         * scheduling it would keep her calendar up to date — and typing the date
         * into the step reached the phase and stopped there. So the two ways of
         * dating a procedure now do the same thing.
         *
         * Before the phase write, not after, because the appointment's id has to
         * ride along in the same payload: `addProcedure` persists
         * `appointmentId` on the procedure, which is what lets a later
         * correction move that appointment instead of booking a second one. It
         * is also the order the scheduling row uses — appointment first, date
         * mirrored onto the phase second.
         *
         * A failure here must not cost her the step. The record is the thing she
         * asked to save; the calendar entry is a courtesy on top, and the phase
         * write below is unconditional.
         */
        const schedulingEntry = getProcedureSchedulingEntry(stepId);

        /**
         * Skipping a procedure retires the appointment it booked.
         *
         * She dates the collection for Tuesday, the appointment is created, and
         * she then learns they are using frozen sperm and skips the step. Without
         * this the Tuesday appointment stays — in her calendar and, once the
         * sharing grant is there, in her partner's — for a procedure the app now
         * records as not happening.
         */
        if (
          schedulingEntry &&
          data.status === "skipped" &&
          !options?.skipCalendarSync
        ) {
          const bookedId = stepData.appointmentId;
          if (typeof bookedId === "string" && bookedId) {
            try {
              await deleteAppointment.mutateAsync(bookedId);
              // Cleared in the same payload, so the phase stops naming an
              // appointment that no longer exists.
              stepData.appointmentId = null;
            } catch (cancelError) {
              console.error(
                "[CycleDetailScreen] could not retire the appointment for skipped step",
                stepId,
                "→",
                cancelError,
              );
            }
          }
        }

        if (
          schedulingEntry &&
          data.status !== "skipped" &&
          !options?.skipCalendarSync
        ) {
          const appointmentPlan = resolveProcedureAppointmentPlan({
            stepId,
            data: stepData,
            dateField: schedulingEntry.dateField,
            todayIso: today,
            timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
            partner: resolvePartnerCounterparty(patient?.counterparties),
          });
          // Only when the appointment is first booked. Asked again on every
          // correction of the date it stops being an offer and becomes a thing
          // she has to dismiss, which is the opposite of the point.
          if (
            appointmentPlan.invitePartnerToConnect &&
            appointmentPlan.action === "create"
          ) {
            setPartnerCalendarPromptVisible(true);
          }
          if (appointmentPlan.action !== "none" && appointmentPlan.startDate) {
            try {
              // All four time fields, always: `createAppointment` throws a
              // ValidationError on a missing `startTime`, `endDate` or `endTime`
              // and `mergeAppointmentFormats` fills in none of them, so a partial
              // window is a write that only ever fails — in the catch below,
              // where she would never see it.
              const shared = {
                cycleId: cycle.id,
                title: schedulingEntry.appointmentTitle,
                consultationType: [schedulingEntry.activity],
                startDate: appointmentPlan.startDate,
                startTime: appointmentPlan.startTime ?? undefined,
                endDate: appointmentPlan.endDate ?? undefined,
                endTime: appointmentPlan.endTime ?? undefined,
                additionalParticipantIds:
                  appointmentPlan.additionalParticipantIds,
              };
              if (appointmentPlan.appointmentId) {
                await updateAppointment.mutateAsync({
                  appointmentId: appointmentPlan.appointmentId,
                  ...shared,
                });
              } else {
                const created = await createAppointment.mutateAsync(shared);
                const createdId = created?.data?.id;
                if (createdId) stepData.appointmentId = createdId;
              }
            } catch (appointmentError) {
              console.error(
                "[CycleDetailScreen] could not sync the calendar for step",
                stepId,
                "→",
                appointmentError,
              );
            }
          }
        }

        const fertilityMetrics =
          Object.keys(stepData).length > 0
            ? buildFertilityMetrics(stepData, existingPhase)
            : undefined;
        const subObjects = buildPhaseSubObjects(
          stepId,
          stepData,
          patientId,
          existingPhase,
        );

        // PGT has no standalone phase in the current API. Persist its
        // patient-entered summary as a typed genetics test result on the
        // existing embryo-development phase, without changing that phase's
        // dates. This keeps skip/report/pending distinct and prevents an
        // optimistic completion from disappearing on reload.
        if (stepId === "pgt_testing") {
          const pgtStatus =
            data.status === "skipped"
              ? "not_performed"
              : data.status === "pending"
                ? "pending"
                : "reported";
          const hasCount =
            stepData.euploidCount !== null &&
            stepData.euploidCount !== undefined &&
            stepData.euploidCount !== "";
          const hasNotes =
            typeof stepData.pgtNotes === "string" &&
            stepData.pgtNotes.trim().length > 0;
          if (pgtStatus === "reported" && !hasCount && !hasNotes) {
            throw new Error("PGT results need a count or note before saving");
          }

          const pgtTestResult = buildPgtTestResult(
            stepData,
            pgtStatus,
            today,
            existingPhase,
          );
          const otherTestResults = (existingPhase?.testResults ?? []).filter(
            (result) => result.name?.toLowerCase() !== "pgt",
          );
          const testResults = [...otherTestResults, pgtTestResult];

          if (existingPhase) {
            await updatePhase({
              cycleId: cycle.id,
              phaseId: existingPhase.id,
              body: { testResults },
            });
          } else {
            await createPhase({
              cycleId: cycle.id,
              body: {
                type: "embryo_development",
                startDate: today,
                endDate: null,
                testResults,
              },
            });
          }
          const phaseQueryKey = phaseKeys.cycle(tenantId ?? "", cycle.id);
          await queryClient.refetchQueries({
            queryKey: phaseQueryKey,
            type: "all",
          });
          const persistedPhase =
            queryClient
              .getQueryData<ApiPhase[]>(phaseQueryKey)
              ?.find((phase) => phase.type === "embryo_development") ?? null;
          if (!pgtTestResultMatches(persistedPhase, pgtTestResult)) {
            throw new Error("PGT decision did not round-trip from the API");
          }
          return;
        }

        let procedureStartDate: string | undefined;
        let procedureStartTime: string | undefined;
        const rawProcDate = (stepData.procedureDate ??
          stepData.transferDate ??
          stepData.collectionDate) as string | null;
        if (rawProcDate && rawProcDate.includes("T")) {
          const d = new Date(rawProcDate);
          if (!isNaN(d.getTime())) {
            const y = d.getFullYear();
            const m = (d.getMonth() + 1).toString().padStart(2, "0");
            const day = d.getDate().toString().padStart(2, "0");
            procedureStartDate = `${y}-${m}-${day}`;
            const hours = d.getHours().toString().padStart(2, "0");
            const minutes = d.getMinutes().toString().padStart(2, "0");
            procedureStartTime = `${hours}:${minutes}`;
          }
        } else if (rawProcDate) {
          procedureStartDate = rawProcDate.split("T")[0]!;
        }

        if (
          data.status === "completed" &&
          stepId === "transfer" &&
          cycle.cycleType === "fet"
        ) {
          if (!procedureStartDate) {
            throw new Error("A transfer date is required before saving");
          }
          if (fetPlanForCycleQuery.isPending) {
            throw new Error("The accepted FET plan is still loading");
          }
          if (fetPlanForCycleQuery.error) {
            throw fetPlanForCycleQuery.error;
          }
          const plan = fetPlanForCycleQuery.data;
          const rawTransferCount = stepData.embryosTransferred;
          const actualTransferCount =
            typeof rawTransferCount === "number"
              ? rawTransferCount
              : typeof rawTransferCount === "string" && rawTransferCount
                ? Number(rawTransferCount)
                : null;
          const clientRequestId =
            fetProcedureReportRequestIdRef.current ?? createIdempotencyKey();
          fetProcedureReportRequestIdRef.current = clientRequestId;
          const transferFacts = {
            actualDate: procedureStartDate,
            ...(procedureStartTime
              ? {
                  actualTime: procedureStartTime,
                  // `procedureStartTime` is local wall-clock, so without this the
                  // backend's "UTC" default stamps it as UTC.
                  timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
                }
              : {}),
            ...(typeof actualTransferCount === "number" &&
            Number.isFinite(actualTransferCount)
              ? { actualTransferCount }
              : {}),
            ...(typeof stepData.notes === "string" && stepData.notes
              ? { notes: stepData.notes }
              : {}),
            clientRequestId,
          };
          // Two cases, not three. The self-report endpoint already decides
          // between "no plan yet", "a plan but no procedure" and "a procedure
          // without a date" — server-side, in one transaction, under a lock on
          // the cycle, and against the version the cycle was actually
          // snapshotted from. Reproducing that decision here meant doing it with
          // less information: a create on a plan whose current version is not the
          // snapshotted one is refused, and a create raced against a still-loading
          // lifecycle query added a second transfer to a cycle that can hold one.
          //
          // The one case it cannot serve is a correction: it answers a replay by
          // returning the dated procedure untouched, which would silently discard
          // an edited date.
          //
          // And one case nobody can serve from here: a procedure row left with no
          // date by an older path. `recordStatus` is read-only over the API — it
          // can be read as `current` or `superseded`, never written — so there is
          // no way to retire such a row and start clean, and the self-report will
          // keep landing on it. Left as is on purpose: the row is empty, so a
          // clinic filing its own transfer on the same cycle simply fills it, and
          // nothing the patient can see is wrong in the meantime. Fixing it would
          // need the backend to expose a way to supersede a procedure.
          // Read fresh rather than from cache. `expectedVersion` comes off this
          // row, and a cached version is stale the moment an earlier correction
          // landed — the backend then refuses with a conflict, the optimistic
          // status rolls back, and the panel drops to the form as if the tap had
          // done nothing. Deciding from a cache that had not answered yet had the
          // same shape of consequence: a second transfer on a cycle that holds
          // one. A save is user-initiated, so one authoritative read is the right
          // price. The query is disabled for a partner-viewed cycle, where the
          // refetch is a no-op and there is nothing for a supporter to correct.
          if (fetBetaLifecycleQuery.error) {
            throw fetBetaLifecycleQuery.error;
          }
          const standingProcedure = isPartnerCycle
            ? undefined
            : (await fetBetaLifecycleQuery.refetch()).data?.procedure;
          // Only her own report is hers to correct — the reasons live with the
          // predicate, which the hub's lock reads too so the screen refuses
          // exactly what the save refuses.
          const isHerOwnReport = isPatientOwnedFETProcedure(standingProcedure);
          if (standingProcedure?.actualDate && plan && isHerOwnReport) {
            await updateFETProcedureReport.mutateAsync({
              planId: plan.id,
              procedureId: standingProcedure.id,
              cycleId: cycle.id,
              expectedVersion: standingProcedure.version,
              ...transferFacts,
            });
          } else {
            await reportFETTransferSelf.mutateAsync({
              cycleId: cycle.id,
              ...transferFacts,
            });
          }
          fetProcedureReportRequestIdRef.current = null;
          await queryClient.refetchQueries({
            queryKey: ["cycle-phases"],
            type: "all",
          });
          return;
        }

        if (data.status === "completed") {
          const effectiveStart =
            procedureStartDate ?? existingPhase?.startDate ?? today;
          const nextDay = (dateStr: string) => {
            const d = new Date(dateStr + "T00:00:00");
            d.setDate(d.getDate() + 1);
            const y = d.getFullYear();
            const m = (d.getMonth() + 1).toString().padStart(2, "0");
            const day = d.getDate().toString().padStart(2, "0");
            return `${y}-${m}-${day}`;
          };
          // Date in the future ⇒ user is *scheduling* the step, not
          // completing it. Persist the date as startDate but leave endDate
          // null so deriveStepProgress returns "scheduled" and the user
          // can come back to confirm completion on the day.
          const isFutureStart = effectiveStart > today;
          const endDate: string | null = isFutureStart
            ? null
            : nextDay(effectiveStart);

          if (stepId === "trigger_shot" || stepId === "freezing") {
            const isSpermFreezingFreezing =
              stepId === "freezing" && cycle.cycleType === "sperm_freezing";
            const stepDataNotes = isSpermFreezingFreezing
              ? JSON.stringify(stepData)
              : undefined;
            if (existingPhase) {
              await updatePhase({
                cycleId: cycle.id,
                phaseId: existingPhase.id,
                body: {
                  ...(stepId === "freezing" && { endDate }),
                  ...(stepDataNotes != null && { notes: stepDataNotes }),
                  ...(fertilityMetrics && { fertilityMetrics }),
                  ...subObjects,
                },
              });
            } else {
              await createPhase({
                cycleId: cycle.id,
                body: {
                  type: phaseType,
                  startDate: effectiveStart,
                  ...(stepId === "freezing" && { endDate }),
                  ...(stepDataNotes != null && { notes: stepDataNotes }),
                  ...(fertilityMetrics && { fertilityMetrics }),
                  ...subObjects,
                },
              });
            }
            /**
             * Carry a lab note from the outcome tab onto the cycle's record,
             * naming the row it belongs to.
             *
             * This fired unconditionally and wrote `{ notes: null }` with no
             * `id`. The reported symptom was that saving frozen eggs summoned
             * "you made it to the end" and that "Got it" came undone. The cause
             * is not the one the comment here used to give: the server routes an
             * outcome body with no id to an INSERT, `outcomes` has no unique
             * index on `cycle_id`, and reads order by `recorded_at` — so every
             * save added a fresh outcome row that stood in front of the
             * acknowledged one. An update keyed by `id` writes only the columns
             * it carries, which is why `acknowledgedAt` — absent from the PATCH
             * surface entirely — survives one.
             *
             * Gating it on a non-empty note then made clearing a note
             * impossible. `outcomeNoteNeedsWriting` decides that; `outcomeWriteFor`
             * supplies the id.
             */
            const freezingNotes =
              typeof stepData.notes === "string" && stepData.notes.trim()
                ? stepData.notes
                : null;
            if (
              stepId === "freezing" &&
              cycle.cycleType !== "sperm_freezing" &&
              outcomeNoteNeedsWriting({
                recorded: cycle.outcome,
                note: freezingNotes,
              })
            ) {
              await updateCycleMutation.mutateAsync({
                cycleId: cycle.id,
                data: {
                  outcome: outcomeWriteFor(cycle.outcome, {
                    notes: freezingNotes,
                  }),
                },
              });
            }
          } else if (existingPhase) {
            const needsNotesJson =
              stepId === "sperm_collection" ||
              (cycle.cycleType === "sperm_freezing" && stepId === "analysis");
            const stepDataNotes = needsNotesJson
              ? JSON.stringify(stepData)
              : undefined;
            await updatePhase({
              cycleId: cycle.id,
              phaseId: existingPhase.id,
              body: {
                startDate:
                  procedureStartDate ??
                  existingPhase.startDate ??
                  effectiveStart,
                ...(procedureStartTime !== undefined && {
                  startTime: procedureStartTime,
                }),
                endDate,
                ...(stepDataNotes != null && { notes: stepDataNotes }),
                fertilityMetrics,
                ...subObjects,
              },
            });
          } else {
            const needsNotesJson =
              stepId === "sperm_collection" ||
              (cycle.cycleType === "sperm_freezing" && stepId === "analysis");
            const stepDataNotes = needsNotesJson
              ? JSON.stringify(stepData)
              : undefined;
            await createPhase({
              cycleId: cycle.id,
              body: {
                type: phaseType,
                startDate: effectiveStart,
                ...(procedureStartTime !== undefined && {
                  startTime: procedureStartTime,
                }),
                endDate,
                ...(stepDataNotes != null && { notes: stepDataNotes }),
                fertilityMetrics,
                ...subObjects,
              },
            });
          }
        } else if (data.status === "scheduled") {
          // A scheduled appointment records the intent, not the outcome —
          // even if the chosen day is today or in the past. Persist the
          // date as the phase startDate and leave endDate null so
          // `deriveStepProgress` reads it as "scheduled" and the hub keeps
          // offering the log-outcome affordance instead of auto-advancing.
          const effectiveStart =
            procedureStartDate ?? existingPhase?.startDate ?? today;
          if (existingPhase) {
            await updatePhase({
              cycleId: cycle.id,
              phaseId: existingPhase.id,
              body: {
                startDate: effectiveStart,
                ...(procedureStartTime !== undefined && {
                  startTime: procedureStartTime,
                }),
                endDate: null,
              },
            });
          } else {
            await createPhase({
              cycleId: cycle.id,
              body: {
                type: phaseType,
                startDate: effectiveStart,
                ...(procedureStartTime !== undefined && {
                  startTime: procedureStartTime,
                }),
                endDate: null,
              },
            });
          }
        } else if (data.status === "skipped") {
          if (existingPhase) {
            await updatePhase({
              cycleId: cycle.id,
              phaseId: existingPhase.id,
              body: { startDate: today, endDate: today },
            });
          } else {
            await createPhase({
              cycleId: cycle.id,
              body: { type: phaseType, startDate: today, endDate: today },
            });
          }
          // Cascade: skipping the transfer ("freezing all embryos") leaves
          // nothing to support with luteal meds, so the luteal_phase step
          // is dead. Auto-write a matching skip so the cycle reads as done.
          if (stepId === "transfer") {
            const lutealPhase = (activeCycle.phases ?? []).find(
              (p: Phase) => p.type === "luteal_phase",
            );
            if (lutealPhase) {
              await updatePhase({
                cycleId: cycle.id,
                phaseId: lutealPhase.id,
                body: { startDate: today, endDate: today },
              });
            } else {
              await createPhase({
                cycleId: cycle.id,
                body: {
                  type: "luteal_phase",
                  startDate: today,
                  endDate: today,
                },
              });
            }
          }
        } else if (data.status === "pending") {
          if (stepId === "trigger_shot") {
            if (existingPhase) {
              await updatePhase({
                cycleId: cycle.id,
                phaseId: existingPhase.id,
                body: { triggerShot: null },
              });
            }
          } else if (stepId === "freezing") {
            if (existingPhase) {
              const keepMetrics = (existingPhase.fertilityMetrics ?? [])
                .filter(
                  (m: ApiFertilityMetric) =>
                    m.type &&
                    m.type !== "m2_count" &&
                    m.type !== "frozen_embryo",
                )
                .map((m: ApiFertilityMetric) => ({
                  id: m.id,
                  type: m.type!,
                  value: m.value as number | null,
                }));
              await updatePhase({
                cycleId: cycle.id,
                phaseId: existingPhase.id,
                body: {
                  fertilityMetrics: keepMetrics.length > 0 ? keepMetrics : [],
                },
              });
            }
            await updateCycleMutation.mutateAsync({
              cycleId: cycle.id,
              data: { outcome: { notes: null } },
            });
          } else if (existingPhase) {
            await updatePhase({
              cycleId: cycle.id,
              phaseId: existingPhase.id,
              body: { endDate: null },
            });
          }
        } else if (data.data) {
          if (
            cycle.cycleType === "fet" &&
            stepId === "transfer" &&
            !procedureStartDate
          ) {
            throw new Error("A transfer date is required before saving");
          }
          const needsNotesJson =
            stepId === "sperm_collection" ||
            (cycle.cycleType === "sperm_freezing" && stepId === "analysis");
          const stepDataNotes = needsNotesJson
            ? JSON.stringify(stepData)
            : undefined;
          if (existingPhase) {
            await updatePhase({
              cycleId: cycle.id,
              phaseId: existingPhase.id,
              body: {
                ...(procedureStartDate && {
                  startDate: procedureStartDate,
                }),
                ...(procedureStartTime !== undefined && {
                  startTime: procedureStartTime,
                }),
                ...(stepDataNotes != null && { notes: stepDataNotes }),
                fertilityMetrics,
                ...subObjects,
              },
            });
          } else {
            await createPhase({
              cycleId: cycle.id,
              body: {
                type: phaseType,
                startDate: procedureStartDate ?? today,
                ...(procedureStartTime !== undefined && {
                  startTime: procedureStartTime,
                }),
                ...(stepDataNotes != null && { notes: stepDataNotes }),
                fertilityMetrics,
                ...subObjects,
              },
            });
          }
        }
        await queryClient.refetchQueries({
          queryKey: ["cycle-phases"],
          type: "all",
        });
      } catch (e) {
        console.error(
          "[CycleDetailScreen] handleUpdateStepProgress failed for step",
          stepId,
          "with data",
          data,
          "→",
          e,
        );
        // A preceding write may have committed even when a later coupled
        // write failed (or its response was lost). Reconcile the phase query
        // before handing the error back to the hub so a retry can PATCH the
        // confirmed phase instead of POSTing a duplicate.
        await queryClient
          .refetchQueries({
            queryKey: ["cycle-phases"],
            type: "all",
          })
          .catch(() => undefined);
        // Re-throw so the hub card can revert its optimistic state and surface
        // the failure instead of showing the step as saved.
        throw e;
      }
    },
    [
      createAppointment,
      updateAppointment,
      deleteAppointment,
      patient?.counterparties,
      cycle,
      hubPhases,
      activeCycle.phases,
      createPhase,
      updatePhase,
      patientId,
      tenantId,
      updateCycleMutation,
      queryClient,
      fetPlanForCycleQuery,
      reportFETTransferSelf,
      updateFETProcedureReport,
      isPartnerCycle,
      fetBetaLifecycleQuery,
      fetProcedureReportRequestIdRef,
    ],
  );

  const handleRecordEggRetrievalOutcome = useCallback(
    async (saved: SpecimenPrefill) => {
      const retrievalData: Record<string, string | number | boolean | null> =
        {};
      const retrievalDate = saved.retrievalDate || saved.freezeDate;
      if (retrievalDate) retrievalData.procedureDate = retrievalDate;
      if (saved.totalRetrieved != null) {
        retrievalData.eggsCollected = saved.totalRetrieved;
      }

      if (Object.keys(retrievalData).length > 0) {
        await handleUpdateStepProgress("egg_retrieval", {
          status: "completed",
          data: retrievalData,
        });
      }

      // Egg freezing's Outcome tab shares the retrieval phase. Once the
      // specimen intake has recorded frozen eggs, complete that tab from the
      // same saved values instead of asking the patient to enter them again.
      const freezingData: Record<string, string | number | boolean | null> = {};
      if (saved.matureMII != null) {
        freezingData.matureEggs = saved.matureMII;
      }
      if (saved.numberFrozen != null) {
        freezingData.eggsFrozen = saved.numberFrozen;
      }
      if (saved.freezeDate) {
        freezingData.freezeDate = saved.freezeDate;
      }
      if (Object.keys(freezingData).length > 0) {
        await handleUpdateStepProgress("freezing", {
          status: "completed",
          data: freezingData,
        });
      }

      /**
       * Take her to what she just recorded.
       *
       * The specimen intake is opened from the retrieval step, and it leaves her
       * there — while the counts she entered are displayed on the journey's last
       * step, which is also where she can correct them. So the save appeared to
       * do nothing, and the retrieval step went on offering to record results
       * she had already given.
       *
       * The last step of the flow rather than a step id: egg and sperm freezing
       * end on `freezing`, embryo freezing has no such step and ends on embryo
       * development or PGT.
       */
      const closingStepId = steps[steps.length - 1]?.id;
      if (closingStepId) {
        treatmentHubRef.current?.selectStep(closingStepId);
      }
    },
    [handleUpdateStepProgress, steps],
  );

  const handleCompletePreservation = useCallback(async () => {
    if (!cycle?.id) return;

    // Transfer-bearing cycles enter the two-week wait — DO NOT close. A
    // freeze-all/no-transfer cycle instead closes as complete below; it must
    // never receive a synthetic luteal phase or beta date.
    if (treatmentGoal === "pregnancy") {
      try {
        const today = toLocalDateString(new Date());
        const latestPhases =
          queryClient.getQueryData<ApiPhase[]>(
            phaseKeys.cycle(tenantId ?? "", cycle.id),
          ) ?? hubPhases;
        const performedProcedure = findLatestPerformedProcedurePhase(
          latestPhases.filter(
            (phase) => phase.startDate && phase.startDate <= today,
          ),
          ["transfer", "insemination"],
        );
        /*
         * No confirmed transfer, so there is no wait to enter — and closing is
         * not this function's decision to take. It used to close the cycle here
         * outright: `status: "closed"`, today's `endDate`, a null outcome and a
         * reset to Care overview, with nothing asked. Every caller now gates on
         * `hasConfirmedTransfer` before reaching this, but that gate reads the
         * render's phases while this reads a fresher cache, so the two can
         * disagree — a transfer deleted between render and tap landed straight
         * in that close. Ending a cycle goes through the dialog that asks.
         */
        if (!hasConfirmedPregnancyProcedure(latestPhases)) {
          setEndCycleMode("complete");
          setEndCycleVisible(true);
          return;
        }
        if (!performedProcedure?.startDate) {
          Alert.alert(
            t("care.treatment_hub.waiting_requires_performed_title"),
            t("care.treatment_hub.waiting_requires_performed_message"),
          );
          return;
        }
        const existingLuteal = latestPhases.find(
          (p) => p.type === "luteal_phase",
        );
        // Anchor the wait window on the transfer / insemination date —
        // beta hCG tests are scheduled relative to that, not relative to
        // when the user happens to tap "Enter Waiting Phase". For IVF /
        // ICSI / FET we look at the `transfer` phase; for IUI / OI it's
        // `insemination`. A missing performed procedure is blocked above.
        const transferDate = performedProcedure.startDate;
        const start = transferDate;
        // Default test date = transfer + 14 days. If the user previously
        // set a real test date (window ≥ 7 days), preserve it so repeated
        // clicks don't overwrite a customized choice.
        const defaultEnd = (() => {
          const d = new Date(start + "T00:00:00");
          d.setDate(d.getDate() + 14);
          // toLocalDateString avoids the toISOString() UTC shift that
          // would silently subtract a day for users east of UTC and
          // produce a 13-day window instead of 14.
          return toLocalDateString(d);
        })();
        const existingWindow =
          existingLuteal?.startDate && existingLuteal?.endDate
            ? Math.round(
                (new Date(existingLuteal.endDate + "T00:00:00").getTime() -
                  new Date(existingLuteal.startDate + "T00:00:00").getTime()) /
                  (1000 * 60 * 60 * 24),
              )
            : 0;
        const endDate =
          existingWindow >= 7 ? existingLuteal!.endDate! : defaultEnd;
        if (existingLuteal?.id) {
          await updatePhase({
            cycleId: cycle.id,
            phaseId: existingLuteal.id,
            body: { startDate: start, endDate },
          });
        } else {
          await createPhase({
            cycleId: cycle.id,
            body: {
              type: "luteal_phase",
              startDate: start,
              endDate,
            },
          });
        }
        await queryClient.refetchQueries({
          queryKey: ["cycle-phases"],
          type: "all",
        });
      } catch {
        await queryClient
          .refetchQueries({
            queryKey: ["cycle-phases"],
            type: "all",
          })
          .catch(() => undefined);
        Alert.alert(
          t("care.treatment_hub.save_failed_title"),
          t("care.treatment_hub.save_failed_message"),
        );
      }
      return;
    }

    // Preservation cycles — terminal close, navigate home.
    try {
      // Routed through `useCompleteCycle` rather than a bare status PATCH so the
      // close creates an outcome row. Without one there is nothing to
      // acknowledge, and the ending never reaches Home's closing message: a
      // sperm-freezing cycle closed this way was silently ending with no record
      // of *when*, unlike the same cycle closed through CompleteCycleModal.
      await createSnapshot.mutateAsync(cycle.id).catch(() => {
        // Best effort: a missing snapshot degrades the past-cycle view but must
        // not block the close.
      });
      // Reuse the existing outcome id when there is one. The cycle/outcome
      // relation is 1:1, so omitting it forces an INSERT and the server rejects
      // it with a unique-constraint error — see `useRecordCycleOutcome`.
      await completeCycle.mutateAsync({
        cycleId: cycle.id,
        outcomeId: cycleOutcome?.id,
        // A PATCH replaces the whole outcome sub-object, so anything already
        // stored has to be sent back or it is destroyed.
        existingNotes: cycleOutcome?.notes,
        existingDateRecorded: cycleOutcome?.dateRecorded,
      });
      await Promise.all([
        queryClient.refetchQueries({ queryKey: ["active-cycle"], type: "all" }),
        queryClient.refetchQueries({ queryKey: ["cycles"], type: "all" }),
        queryClient.refetchQueries({ queryKey: ["treatment-summaries"] }),
      ]);
      navigation.reset({
        index: 0,
        routes: [{ name: "CareOverview" }],
      });
    } catch {
      // Keep the completion view in place. Its CTA remains available so the
      // patient can retry the close operation without being routed to a
      // screen that implies the cycle was completed.
      Alert.alert(
        t("care.treatment_hub.save_failed_title"),
        t("care.treatment_hub.save_failed_message"),
      );
    }
  }, [
    cycle,
    treatmentGoal,
    hubPhases,
    createPhase,
    updatePhase,
    completeCycle,
    createSnapshot,
    cycleOutcome?.id,
    cycleOutcome?.notes,
    cycleOutcome?.dateRecorded,
    queryClient,
    navigation,
    tenantId,
    t,
  ]);

  // Persist the user's chosen pregnancy test date onto luteal_phase.endDate.
  // Triggered from WaitingPhaseView's date picker.
  const handleSetTestDate = useCallback(
    async (date: string) => {
      if (!cycle?.id || !date) return;
      try {
        setIsSavingTestDate(true);
        setPendingTestDate(date);
        setTestDateSaveError(null);
        const today = toLocalDateString(new Date());
        const latestPhases =
          queryClient.getQueryData<ApiPhase[]>(
            phaseKeys.cycle(tenantId ?? "", cycle.id),
          ) ?? hubPhases;
        const existing = latestPhases.find((p) => p.type === "luteal_phase");
        if (existing?.id) {
          await updatePhase({
            cycleId: cycle.id,
            phaseId: existing.id,
            body: {
              startDate: existing.startDate ?? today,
              endDate: date,
            },
          });
        } else {
          await createPhase({
            cycleId: cycle.id,
            body: {
              type: "luteal_phase",
              startDate: today,
              endDate: date,
            },
          });
        }
        await queryClient.refetchQueries({
          queryKey: ["cycle-phases"],
          type: "all",
        });
        setPendingTestDate(null);
      } catch {
        await queryClient
          .refetchQueries({
            queryKey: ["cycle-phases"],
            type: "all",
          })
          .catch(() => undefined);
        setTestDateSaveError(
          t("care.treatment_hub.waiting_phase.test_date_save_failed"),
        );
      } finally {
        setIsSavingTestDate(false);
      }
    },
    [cycle?.id, hubPhases, createPhase, updatePhase, queryClient, tenantId, t],
  );

  // Submit the beta result modal. FET writes to the accepted typed beta order
  // and remains an open result series; older cycle types retain their typed
  // test-result plus outcome compatibility path.
  const handleConfirmBetaResult = useCallback(
    async ({
      type,
      resultStatus,
      pregnancyOutcome,
      miscarriageOutcome,
      biochemicalPregnancyObserved,
      notes,
      betaHcgValue,
      collectionAt,
      collectionTimeUnknown,
      timeZone,
      sourceDocumentId,
      mode,
    }: {
      mode: "cycle_outcome" | "fet_beta_order";
      type: "pregnancy" | "no_pregnancy" | "miscarriage" | null;
      resultStatus: CycleOutcomeResultStatus;
      pregnancyOutcome?: "live_birth" | "multiple_live_birth" | null;
      miscarriageOutcome?:
        | "complete_miscarriage"
        | "incomplete_miscarriage"
        | "missed_miscarriage"
        | "still_birth"
        | "recurrent_miscarriage"
        | "chemical_pregnancy"
        | "ectopic_pregnancy"
        | "anembryonic_pregnancy"
        | null;
      biochemicalPregnancyObserved?: boolean | null;
      notes: string;
      betaHcgValue?: number | null;
      collectionAt?: string | null;
      collectionTimeUnknown?: boolean;
      timeZone?: string;
      sourceDocumentId?: string | null;
    }) => {
      if (!cycle?.id) return;
      try {
        const performedProcedure = findLatestPerformedProcedurePhase(
          hubPhases,
          ["transfer", "insemination"],
        );
        if (betaHcgValue == null) {
          throw new Error("Beta hCG value is required");
        }
        if (mode === "fet_beta_order") {
          if (cycleType !== "fet") {
            throw new Error("FET beta results require an FET cycle");
          }
          if (fetBetaLifecycleQuery.isPending) {
            throw new FETBetaOrderLookupPendingError();
          }
          if (fetBetaLifecycleQuery.error) {
            throw fetBetaLifecycleQuery.error;
          }
          const lifecycle = fetBetaLifecycleQuery.data;
          if (!lifecycle) {
            throw new FETBetaOrderUnavailableError();
          }
          let order = selectRecordableFETBetaOrder(lifecycle);
          if (!order) {
            const orderRequestId =
              fetBetaOrderRequestIdRef.current ?? createIdempotencyKey();
            fetBetaOrderRequestIdRef.current = orderRequestId;
            order = await createFETBetaOrder.mutateAsync({
              cycleId: cycle.id,
              procedureId: lifecycle.procedure.id,
              status: "collected",
              orderedAt: new Date().toISOString(),
              clientRequestId: orderRequestId,
            });
          }
          const clientRequestId =
            fetBetaResultRequestIdRef.current ?? createIdempotencyKey();
          fetBetaResultRequestIdRef.current = clientRequestId;
          await recordFETBetaResult.mutateAsync({
            cycleId: cycle.id,
            procedureId: lifecycle.procedure.id,
            orderId: order.id,
            value: betaHcgValue,
            collectionAt,
            collectionTimeUnknown,
            timeZone,
            sourceDocumentId,
            clientRequestId,
          });
          await queryClient.refetchQueries({
            queryKey: fetBetaLifecycleQueryKey(patientId, cycle.id),
            type: "all",
          });
        }
        // The FET beta ledger is the value's home on an FET cycle, so no legacy
        // test-result row is written for it — storing the number twice would let
        // the two disagree. The outcome below is still recorded either way: it is
        // what ends the wait and lets the cycle close, and skipping it left an
        // FET cycle sitting on a countdown with its result already in hand.
        let sourceRecordId: string | null = null;
        if (mode !== "fet_beta_order") {
          sourceRecordId = betaTestResult?.id ?? null;
          if (!sourceRecordId) {
            const result = await createBetaTestResult.mutateAsync(
              buildBetaTestResultPayload({
                cycleId: cycle.id,
                phaseId:
                  hubPhases.find((phase) => phase.type === "luteal_phase")
                    ?.id ?? null,
                value: betaHcgValue,
                date: toLocalDateString(new Date()),
                notes,
              }),
            );
            sourceRecordId = result.data.id;
            setBetaTestResult({ id: sourceRecordId, value: betaHcgValue });
          } else if (betaTestResult?.value !== betaHcgValue) {
            // The outcome names this row as its numeric source, so a corrected
            // value has to reach the row too — otherwise the record says 120 in
            // the outcome and still says 12 in the result it points at.
            await updateBetaTestResult.mutateAsync({
              testResultId: sourceRecordId,
              data: buildBetaTestResultUpdate({ value: betaHcgValue, notes }),
            });
            setBetaTestResult({ id: sourceRecordId, value: betaHcgValue });
          }
        }
        // One request id per record being written. A retry with a corrected
        // value keeps it, so a first attempt whose response was lost is
        // recognised as a replay rather than stored a second time.
        const requestId = resolveOutcomeWriteRequestId(
          betaOutcomeRequestIdRef.current,
          outcomeWriteTarget({ outcomeId: cycleOutcome?.id ?? null }),
          createIdempotencyKey,
        );
        betaOutcomeRequestIdRef.current = requestId;
        // A state she reported is final: a lab value may not undo a loss or a
        // birth she has already recorded. Only the update-pregnancy step changes
        // the state. The value itself is still saved either way.
        const protectedEnding = endingAfterBetaWrite(cycleOutcome, {
          type: betaWritableEnding(type) ?? null,
          pregnancyOutcome,
          miscarriageOutcome,
        });
        await recordCycleOutcome.mutateAsync({
          cycleId: cycle.id,
          outcomeId: cycleOutcome?.id ?? null,
          type: protectedEnding.type,
          resultStatus,
          sourceKind: PATIENT_BETA_OUTCOME_SOURCE_KIND,
          sourceRecordId,
          sourceDocumentId: sourceDocumentId ?? null,
          clientRequestId: requestId.requestId,
          procedureType:
            cycleType === "iui" || cycleType === "oi"
              ? "insemination"
              : "embryo_transfer",
          procedureModality:
            cycleType === "fet"
              ? "fet"
              : cycleType === "iui"
                ? "iui"
                : cycleType === "oi"
                  ? "oi"
                  : cycleType === "icsi"
                    ? "icsi"
                    : "ivf",
          procedureDate: performedProcedure?.startDate ?? null,
          procedureTime: performedProcedure?.startTime ?? null,
          pregnancyOutcome: protectedEnding.pregnancyOutcome,
          miscarriageOutcome: protectedEnding.miscarriageOutcome,
          biochemicalPregnancyObserved: biochemicalPregnancyObserved ?? null,
          notes,
          // "Update result" reaches this a second time — a repeat beta after a
          // borderline or first positive. That repeat is a correction, which
          // replaces the whole record, so the earlier value has to be sent back or
          // it is gone: the beta number lives only in `notes`.
          existingNotes: cycleOutcome?.notes,
          existingDateRecorded: cycleOutcome?.dateRecorded,
        });
        await Promise.all([
          queryClient.refetchQueries({
            queryKey: ["active-cycle"],
            type: "all",
          }),
          queryClient.refetchQueries({ queryKey: ["cycles"], type: "all" }),
        ]);
        // Cleared only once the whole write has landed. Clearing them inside the
        // FET block meant a failed outcome write — which keeps the modal open and
        // invites a retry — re-entered the beta block with fresh ids and appended
        // a second `current` result for one draw. Nothing refuses that.
        fetBetaOrderRequestIdRef.current = null;
        fetBetaResultRequestIdRef.current = null;
        setBetaResultError(null);
        // Modal stays open and transitions to its empathetic confirmation
        // step. Navigation happens when the user taps Done — see
        // handleBetaResultComplete.
      } catch (e) {
        // Keep API details out of patient-facing copy. The modal remains open
        // with the entered result so this same outcome write can be retried —
        // except for a supporter, whose refusal no retry can fix, so the message
        // names whose result it is instead.
        setBetaResultError(
          betaResultErrorMessage(
            e,
            t,
            {
              isSupporting: perspective === "partner",
              cycleOwnerName: partnerDisplayName,
            },
            cycleOutcome,
          ),
        );
        throw e;
      }
    },
    [
      cycle?.id,
      // The whole record now, not three of its fields: its provenance decides
      // what a refusal may be attributed to, and depending on the object covers
      // the id, notes and date the write itself reads.
      cycleOutcome,
      cycleType,
      hubPhases,
      betaTestResult,
      createBetaTestResult,
      updateBetaTestResult,
      createFETBetaOrder,
      recordFETBetaResult,
      recordCycleOutcome,
      queryClient,
      patientId,
      fetBetaLifecycleQuery,
      fetBetaOrderRequestIdRef,
      fetBetaResultRequestIdRef,
      perspective,
      partnerDisplayName,
      t,
    ],
  );

  // User finished reading the confirmation content and tapped Done. Dismiss the
  // modal and leave them on the cycle, where the result they just recorded is
  // now shown against it along with the action that closes it.
  //
  // This used to `navigation.reset` to Care home. Recording also closed the
  // cycle at the time, so the patient went from typing a number to a wiped
  // navigation stack in one step, with no way back to the cycle they had just
  // ended — the abruptness this flow was reworked to fix.
  const handleBetaResultComplete = useCallback(() => {
    setBetaResultVisible(false);
    setBetaResultError(null);
    setBetaTestResult(null);
    fetBetaOrderRequestIdRef.current = null;
    fetBetaResultRequestIdRef.current = null;
    betaOutcomeRequestIdRef.current = null;
  }, []);

  /**
   * Close a cycle whose ending is already recorded.
   *
   * Opens the shared end-cycle modal rather than closing directly, so a cycle is
   * ended through one flow wherever the patient starts it — here, the Care page
   * CTA, or the hero on Home. The modal preselects the recorded ending, so this
   * confirms it instead of asking for it again, and it owns the snapshot and the
   * navigation that follow.
   */
  const handleCloseRecordedCycle = useCallback(() => {
    setEndCycleVisible(true);
  }, []);

  const { height: windowHeight } = useWindowDimensions();
  const scrollRef = useRef<ScrollView>(null);
  /** Where the treatment hub starts, measured rather than assumed. */
  const [hubOffsetY, setHubOffsetY] = useState<number | null>(null);
  /**
   * The step an arrival asked for, kept once the param is spent.
   *
   * The param is cleared as soon as it is read, for two reasons that pull the
   * same way. It has to be cleared, or the focus effect fires on every return to
   * this screen — from Documents, from lab results, from the cryo vault, all
   * reachable further down this very page — and drags her back to the hub,
   * losing her place. And it cannot merely be read, or the hub would lose the
   * step she came for the moment it goes. So the request outlives the param, and
   * only the scroll is one-shot.
   */
  const [hubStepRequest, setHubStepRequest] = useState<string | null>(null);
  /**
   * Counts arrivals, because the id alone cannot.
   *
   * The request deliberately outlives the param (see above), so a second arrival
   * for the same step sets `hubStepRequest` to the value it already holds — no
   * prop change, nothing for the hub to react to. She pressed "record my beta",
   * came back to the hub, tapped another tab, pressed it again, and the page
   * scrolled to a hub still sitting on the tab she had chosen.
   */
  const [hubStepRequestCount, setHubStepRequestCount] = useState(0);
  const pendingHubScrollRef = useRef(false);

  useEffect(() => {
    if (route.params?.focus !== "outcome") return;
    setHubStepRequest("outcome");
    setHubStepRequestCount((count) => count + 1);
    pendingHubScrollRef.current = true;
    navigation.setParams({ focus: undefined });
  }, [navigation, route.params?.focus]);

  // Not animated: she asked to be somewhere, and watching the page travel there
  // is not part of the answer.
  useFocusEffect(
    useCallback(() => {
      if (!pendingHubScrollRef.current || hubOffsetY === null) return;
      pendingHubScrollRef.current = false;
      scrollRef.current?.scrollTo({
        y: Math.max(0, hubOffsetY - 8),
        animated: false,
      });
    }, [hubOffsetY]),
  );

  const todayStr = useMemo(() => getTodayString(), []);
  // ── API hooks (replacing hardcoded data) ──
  const { data: apiMedications } = usePatientMedications();

  // Reconcile phase.startDate against the earliest medication actually
  // attached to that phase. ensurePhaseExists has to stamp *something*
  // when it creates a phase from an "Add medication" tap, and `today` is
  // wrong whenever the patient is back-filling a regimen that began
  // earlier (or before the cycle was even registered in the app).
  // Pulling the floor down to the earliest med makes "Started X · Day N"
  // in the hub, calendar, and timeline reflect the patient's truth.
  useEffect(() => {
    if (!cycle?.id || !apiMedications || hubPhases.length === 0) return;
    for (const phase of hubPhases) {
      if (!phase.id) continue;
      const meds = apiMedications.filter(
        (m) =>
          m.cycleId === cycle.id &&
          m.phaseId === phase.id &&
          typeof m.startDate === "string" &&
          m.startDate.length > 0,
      );
      if (meds.length === 0) continue;
      let earliest = meds[0]?.startDate ?? null;
      for (const m of meds) {
        if (m.startDate && (!earliest || m.startDate < earliest)) {
          earliest = m.startDate;
        }
      }
      if (earliest && (!phase.startDate || earliest < phase.startDate)) {
        updatePhase({
          cycleId: cycle.id,
          phaseId: phase.id,
          body: { startDate: earliest },
        }).catch((err) => {
          console.warn(
            "[CycleDetailScreen] phase startDate backfill failed:",
            err,
          );
        });
      }
    }
  }, [cycle?.id, apiMedications, hubPhases, updatePhase]);

  const handleEditMedicationById = useCallback(
    (medicationId: string, fallbackName?: string) => {
      const pm = apiMedications?.find((p) => p.id === medicationId);
      if (!pm) return;
      const timeTaken = Array.isArray(pm.timeTaken)
        ? (pm.timeTaken as string[])
        : [];
      setEditMed({
        id: pm.id,
        name: pm.medication?.name ?? fallbackName ?? "",
        dose: pm.dose,
        doseUnit: pm.doseUnit,
        route: pm.route ?? null,
        form: pm.form ?? null,
        timeTaken,
        startDate: pm.startDate,
        endDate: pm.endDate ?? null,
        notes: pm.notes,
      });
    },
    [apiMedications],
  );
  const handleEditMedication = useCallback(
    (med: MedicationScheduleItem, period?: CycleProtocolMedicationPeriod) => {
      const groupedMedication = med as CycleProtocolMedication;
      const latestPeriod = [...(groupedMedication.periods ?? [])].sort((a, b) =>
        b.startDate.localeCompare(a.startDate),
      )[0];
      handleEditMedicationById(
        period?.id ?? latestPeriod?.id ?? med.id.slice(0, 36),
        med.name,
      );
    },
    [handleEditMedicationById],
  );
  const { data: apiTestResults } = useTestResults();
  const { data: apiNotes } = usePatientNotes();
  const { data: apiMedicationDoseLogs = [] } = useMedicationDoseLogs({
    startDate: cycle?.startDate ?? undefined,
    endDate: cycle?.endDate ?? undefined,
    enabled: !!cycle?.id,
  });

  // Protocol notes are date-anchored timeline context. Keep legacy protocol
  // adjustment notes readable, but do not let unrelated journal notes leak
  // into a cycle's medication timeline.
  const protocolNotes = useMemo(() => {
    if (!apiNotes || apiNotes.length === 0) return [];
    return [...apiNotes]
      .filter((n) => {
        if (!n.date) return false;
        const isLegacyProtocolNote = n.text?.includes(
          "__protocol_adjustment__",
        );
        const belongsToCycle = n.cycleId === cycle?.id;
        const withinCycle =
          (!cycle?.startDate || n.date >= cycle.startDate) &&
          (!cycle?.endDate || n.date <= cycle.endDate);
        return withinCycle && (belongsToCycle || isLegacyProtocolNote);
      })
      .sort((a, b) => (a.date ?? "").localeCompare(b.date ?? ""))
      .map((n) => ({
        id: n.id,
        date: n.date!,
        dayLabel: "",
        text: n.text ?? "",
      }));
  }, [apiNotes, cycle?.endDate, cycle?.id, cycle?.startDate]);

  // Derive journey phases from API
  const apiJourneyPhases = useMemo(() => {
    if (!activeCycle.phases || activeCycle.phases.length === 0) return null;

    const PHASE_CONTENT: Record<
      string,
      { description: string; details: string[] }
    > = {
      pre_treatment: {
        description: t(
          "care.cycle_detail.phase_content.pre_treatment.description",
        ),
        details: [
          t("care.cycle_detail.phase_content.pre_treatment.detail_1"),
          t("care.cycle_detail.phase_content.pre_treatment.detail_2"),
          t("care.cycle_detail.phase_content.pre_treatment.detail_3"),
          t("care.cycle_detail.phase_content.pre_treatment.detail_4"),
        ],
      },
      protocol: {
        description: t("care.cycle_detail.phase_content.protocol.description"),
        details: [
          t("care.cycle_detail.phase_content.protocol.detail_1"),
          t("care.cycle_detail.phase_content.protocol.detail_2"),
          t("care.cycle_detail.phase_content.protocol.detail_3"),
          t("care.cycle_detail.phase_content.protocol.detail_4"),
        ],
      },
      egg_retrieval: {
        description: t(
          "care.cycle_detail.phase_content.egg_retrieval.description",
        ),
        details: [
          t("care.cycle_detail.phase_content.egg_retrieval.detail_1"),
          t("care.cycle_detail.phase_content.egg_retrieval.detail_2"),
          t("care.cycle_detail.phase_content.egg_retrieval.detail_3"),
          t("care.cycle_detail.phase_content.egg_retrieval.detail_4"),
          t("care.cycle_detail.phase_content.egg_retrieval.detail_5"),
        ],
      },
      fertilization: {
        description: t(
          "care.cycle_detail.phase_content.fertilization.description",
        ),
        details: [
          t("care.cycle_detail.phase_content.fertilization.detail_1"),
          t("care.cycle_detail.phase_content.fertilization.detail_2"),
          t("care.cycle_detail.phase_content.fertilization.detail_3"),
          t("care.cycle_detail.phase_content.fertilization.detail_4"),
          t("care.cycle_detail.phase_content.fertilization.detail_5"),
        ],
      },
      transfer: {
        description: t("care.cycle_detail.phase_content.transfer.description"),
        details: [
          t("care.cycle_detail.phase_content.transfer.detail_1"),
          t("care.cycle_detail.phase_content.transfer.detail_2"),
          t("care.cycle_detail.phase_content.transfer.detail_3"),
          t("care.cycle_detail.phase_content.transfer.detail_4"),
          t("care.cycle_detail.phase_content.transfer.detail_5"),
        ],
      },
      luteal_phase: {
        description: t(
          "care.cycle_detail.phase_content.luteal_phase.description",
        ),
        details: [
          t("care.cycle_detail.phase_content.luteal_phase.detail_1"),
          t("care.cycle_detail.phase_content.luteal_phase.detail_2"),
          t("care.cycle_detail.phase_content.luteal_phase.detail_3"),
          t("care.cycle_detail.phase_content.luteal_phase.detail_4"),
          t("care.cycle_detail.phase_content.luteal_phase.detail_5"),
        ],
      },
    };

    const today = new Date();
    return activeCycle.phases.map((p: Phase) => {
      const type = p.type ?? "protocol";
      const startDate = p.startDate ?? null;
      const endDate = p.endDate ?? null;
      let status: "completed" | "active" | "upcoming" = "upcoming";
      if (endDate && new Date(endDate + "T23:59:59") < today) {
        status = "completed";
      } else if (startDate && new Date(startDate + "T00:00:00") <= today) {
        status = "active";
      }
      const label =
        PHASE_TYPE_LABELS[type as keyof typeof PHASE_TYPE_LABELS] ?? type;
      const content = PHASE_CONTENT[type] ?? {
        description: "",
        details: [],
      };
      return {
        label,
        icon: (type === "protocol"
          ? Syringe
          : type === "egg_retrieval"
            ? Sparkles
            : type === "transfer"
              ? Baby
              : type === "luteal_phase"
                ? Hourglass
                : type === "fertilization"
                  ? Beaker
                  : Target) as LucideIcon,
        status,
        timing: (() => {
          if (!startDate) return "Upcoming";
          const startMs = new Date(startDate + "T00:00:00").getTime();
          const todayMs = new Date(todayStr + "T00:00:00").getTime();
          const startLabel = formatDate(new Date(startMs), locale, {
            month: "short",
            day: "numeric",
          });
          if (status === "completed" && endDate) {
            const endLabel = formatDate(
              new Date(endDate + "T00:00:00"),
              locale,
              { month: "short", day: "numeric" },
            );
            return `${startLabel} – ${endLabel}`;
          }
          if (status === "active") {
            if (!isDurationPhaseType(type)) {
              return t("care.cycle_detail.timeline.started_on", {
                params: { date: startLabel },
              });
            }
            const dayNum = Math.max(
              1,
              Math.round((todayMs - startMs) / DAY_IN_MS) + 1,
            );
            return t("care.cycle_detail.timeline.started_marker", {
              params: { date: startLabel, day: String(dayNum) },
            });
          }
          return t("care.cycle_detail.timeline.starts", {
            params: { date: startLabel },
          });
        })(),
        description: content.description,
        details: content.details,
      };
    });
  }, [activeCycle.phases, todayStr, t, locale]);

  // Derive cycle info from API
  const cycleLabel = activeCycle.cycle
    ? t("care.cycle_detail.cycle_label.with_type", {
        params: {
          type: (activeCycle.cycle.cycleType ?? "IVF").toUpperCase(),
        },
      })
    : t("care.cycle_detail.cycle_label.fallback");
  const cycleStartDate = (activeCycle.cycle as Record<string, unknown>)
    ?.startDate as string | undefined;
  const cycleEndDate = (activeCycle.cycle as Record<string, unknown>)
    ?.endDate as string | undefined;
  const currentCycleProtocolData = useMemo<CycleProtocolData>(() => {
    const medicationRowsByKey = new Map<string, CycleProtocolMedication>();
    const medicationPeriods: CycleProtocolMedicationPeriod[] = [];

    // Protocol retains every medication period that overlaps this cycle. We
    // include:
    //   1. Anything tagged to this cycle (added via the hub).
    //   2. Anything fertility-related the patient logged elsewhere
    //      (cycleId null/missing) — these are still part of the protocol
    //      from the patient's perspective even if they weren't entered
    //      through the hub's "Add medication" flow.
    const FERTILITY_MOLECULES = new Set([
      "hcg",
      "gonadotropin",
      "gnrh_agonist",
      "gnrh_antagonist",
      "progestogen",
      "estrogen",
      "clomiphene_citrate",
      "letrozole",
      "folate_supplement",
    ]);
    for (const medication of apiMedications ?? []) {
      const isThisCycle = medication.cycleId === cycle?.id;
      const isCycleOrphanFertility =
        !medication.cycleId &&
        FERTILITY_MOLECULES.has(medication.medication?.moleculeType ?? "");
      if (!isThisCycle && !isCycleOrphanFertility) continue;
      if (
        cycleStartDate &&
        medication.endDate &&
        medication.endDate < cycleStartDate
      ) {
        continue;
      }
      if (cycleEndDate && medication.startDate > cycleEndDate) continue;
      const name =
        medication.medication?.name ??
        medication.rawMedicationName ??
        t("care.cycle_detail.medication.default_name");
      const dose = formatMedicationDose(medication.dose, medication.doseUnit);
      const times = normalizeMedicationTimes(medication.timeTaken);
      const form = API_FORM_TO_UI[medication.form ?? ""] ?? "pill";
      const route = medication.route ?? "";
      const sinceDay = getCycleDayLabel(
        cycleStartDate ?? null,
        medication.startDate ?? null,
        t,
      );
      const untilDay = getCycleDayLabel(
        cycleStartDate ?? null,
        medication.endDate ?? null,
        t,
      );
      const medicationKey = `${name.trim().toLowerCase()}-${form}-${route}`;
      const existing = medicationRowsByKey.get(medicationKey);
      const period: CycleProtocolMedicationPeriod = {
        id: medication.id,
        name,
        doseLabel: dose || null,
        form,
        route,
        startDate: medication.startDate,
        endDate: medication.endDate ?? null,
        timeTaken: times,
        notes: medication.notes ?? null,
        stopped: /^stopped(?:\s|:|$)/i.test(medication.notes ?? ""),
      };
      medicationPeriods.push(period);
      const historyEntry = dose
        ? {
            cycle: cycleLabel,
            dosage: dose,
            note: times.length
              ? t("care.cycle_detail.medication.scheduled_at", {
                  params: { times: times.join(", ") },
                })
              : undefined,
          }
        : undefined;

      if (existing) {
        const nextDoseBadges = existing.dosageBadges ?? [];
        if (dose && !nextDoseBadges.includes(dose)) {
          nextDoseBadges.push(dose);
        }
        const existingTimes = existing.userContext?.timeTaken ?? [];
        const mergedTimes = Array.from(new Set([...existingTimes, ...times]));
        const existingHistory = existing.userContext?.history ?? [];
        const existingPeriods = existing.periods ?? [];
        const existingStartDate = existing.userContext?.startDate;
        const nextStartDate =
          medication.startDate &&
          (!existingStartDate || medication.startDate < existingStartDate)
            ? medication.startDate
            : existingStartDate;
        const existingEndDate = existing.userContext?.endDate;
        const nextEndDate =
          existingEndDate === null || medication.endDate === null
            ? null
            : medication.endDate &&
                (!existingEndDate || medication.endDate > existingEndDate)
              ? medication.endDate
              : existingEndDate;
        const nextSinceDay = getCycleDayLabel(
          cycleStartDate ?? null,
          nextStartDate ?? null,
          t,
        );
        const nextUntilDay = getCycleDayLabel(
          cycleStartDate ?? null,
          nextEndDate ?? null,
          t,
        );
        existing.dosageBadges = nextDoseBadges;
        existing.dosage = nextDoseBadges[0] ?? existing.dosage;
        existing.time = mergedTimes.join(", ");
        existing.timing = getMedicationTiming(mergedTimes);
        existing.sinceDay = nextSinceDay;
        existing.untilDay = nextUntilDay;
        existing.userContext = {
          ...existing.userContext,
          dosage: nextDoseBadges.join(", "),
          timeTaken: mergedTimes,
          startDate: nextStartDate,
          endDate: nextEndDate,
          sinceDay: nextSinceDay,
          history: historyEntry
            ? [...existingHistory, historyEntry]
            : existingHistory,
        };
        existing.periods = [...existingPeriods, period];
        continue;
      }

      medicationRowsByKey.set(medicationKey, {
        type: "medication",
        id: medication.id,
        name,
        brandName: name,
        dosage: dose,
        dosageBadges: dose ? [dose] : [],
        form,
        route,
        time: times.join(", "),
        timing: getMedicationTiming(times),
        color: WARM,
        recurrence: deriveRecurrence({
          startDate: medication.startDate,
          endDate: medication.endDate,
        }),
        sinceDay,
        untilDay,
        userContext: {
          dosage: dose,
          route,
          selectedForm: medication.form ?? undefined,
          timeTaken: times,
          startDate: medication.startDate ?? undefined,
          endDate: medication.endDate ?? null,
          notes: medication.notes ?? undefined,
          sinceDay,
          history: historyEntry ? [historyEntry] : undefined,
        },
        periods: [period],
      });
    }

    const medicationIds = new Set(medicationPeriods.map((period) => period.id));
    const doseLogs: CycleProtocolDoseLog[] = apiMedicationDoseLogs
      .filter((log) => medicationIds.has(log.medicationId))
      .map((log) => ({
        medicationId: log.medicationId,
        scheduledDate: log.scheduledDate,
        scheduledTime: log.scheduledTime,
        status: log.status === "taken_late" ? "late" : log.status,
      }))
      .filter(
        (log): log is CycleProtocolDoseLog =>
          log.status === "taken" ||
          log.status === "late" ||
          log.status === "missed",
      );

    return {
      id: activeCycle.cycle?.id ?? null,
      startDate: cycleStartDate ?? null,
      endDate: cycleEndDate ?? null,
      medications: Array.from(medicationRowsByKey.values()),
      medicationPeriods,
      doseLogs,
      notes: protocolNotes,
    };
  }, [
    activeCycle.cycle?.id,
    apiMedications,
    apiMedicationDoseLogs,
    cycle?.id,
    cycleLabel,
    cycleEndDate,
    cycleStartDate,
    protocolNotes,
    t,
  ]);
  const currentCycleDocuments = useMemo(
    () => getCycleDocuments(documents, cycle?.id),
    [documents, cycle?.id],
  );
  const currentCycleTestResults = useMemo(
    () =>
      cycle?.id
        ? (apiTestResults ?? []).filter((result) => result.cycleId === cycle.id)
        : [],
    [apiTestResults, cycle?.id],
  );
  const currentCycleDocumentLabel = getCycleDocumentFilterLabel({
    cycleType: displayName,
    cycleNumber,
  });
  const openCurrentCycleDocuments = useCallback(
    (openUpload = false, initialCategory?: string) => {
      if (openUpload) {
        openDocumentUpload({
          defaultCategory: initialCategory ?? "medical_records",
          defaultTags: cycle?.id ? [getCycleDocumentTag(cycle.id)] : [],
        });
        return;
      }

      navigation.navigate("Documents", {
        initialCategory,
        initialDocumentFilter: cycle?.id
          ? getCycleDocumentTag(cycle.id)
          : undefined,
        initialDocumentFilterLabel: currentCycleDocumentLabel,
        openUpload,
      });
    },
    [currentCycleDocumentLabel, cycle?.id, navigation, openDocumentUpload],
  );
  const currentCycleTeamMembers = useMemo(
    () =>
      buildCycleTeamMembers({
        members: cycleCareTeamMembers,
        clinicName: cycle?.provider ?? null,
      }),
    [cycleCareTeamMembers, cycle?.provider],
  );
  const openPatientCareTeam = useCallback(() => {
    setCareTeamModalVisible(false);
    navigation
      .getParent<NavigationProp<MainTabParamList>>()
      ?.getParent<NavigationProp<RootStackParamList>>()
      ?.navigate("Profile", { screen: "ConnectionsDetail" });
  }, [navigation]);

  const handleCycleTeamAction = useCallback(() => {
    setCareTeamModalVisible(true);
  }, []);

  const cycleHeaderState = useCycleHeaderState({
    cycleId: cycle?.id ?? route.params?.cycleId,
    pgtPlan,
  });
  const cycleLabelsForChip = useCycleTypeLabels();
  const currentPhaseLabel = useMemo(() => {
    // Single source of truth — same step the treatment hub considers current.
    const copy = presentCycleHeaderForCare({
      state: cycleHeaderState,
      t,
      locale,
      cycleLabels: cycleLabelsForChip,
    });
    if (!copy.hasActiveCycle) return null;
    return copy.title;
  }, [cycleHeaderState, t, locale, cycleLabelsForChip]);

  // ── Cycle details info ──
  const cycleStartFormatted = useMemo(() => {
    if (!cycle?.startDate) return "\u2014";
    try {
      return formatDateTime(cycle.startDate, locale, {
        month: "short",
        day: "numeric",
        year: "numeric",
      });
    } catch {
      return cycle.startDate;
    }
  }, [cycle?.startDate, locale]);

  const cycleChartTimeline = useMemo<CycleChartTimelineItem[]>(() => {
    const todayMs = getLocalDateMs(todayStr) ?? Date.now();
    const timelineSteps = buildCycleTimelineSteps(steps, cycleType, t);
    // The timeline rows carry ids and copy only, so which steps the cycle
    // decides has to come from the step configs they were built from.
    const derivedProgressStepIds = new Set(
      steps.filter((step) => step.derivedProgress).map((step) => step.id),
    );
    const actualPhaseByType = new Map<string, Phase>();
    for (const phase of activeCycle.phases ?? []) {
      if (!phase.type) continue;
      const existing = actualPhaseByType.get(phase.type);
      const existingStart = getLocalDateMs(existing?.startDate) ?? 0;
      const currentStart = getLocalDateMs(phase.startDate) ?? 0;
      if (!existing || currentStart >= existingStart) {
        actualPhaseByType.set(phase.type, phase);
      }
    }
    const apiPhaseByType = new Map<
      string,
      NonNullable<typeof apiJourneyPhases>[number]
    >();
    (activeCycle.phases ?? []).forEach((phase, index) => {
      const item = apiJourneyPhases?.[index];
      if (phase.type && item) apiPhaseByType.set(phase.type, item);
    });

    const cycleStartMs =
      getLocalDateMs(cycle?.startDate) ??
      getLocalDateMs(activeCycle.phases?.[0]?.startDate) ??
      todayMs;

    return timelineSteps.map((step) => {
      const phaseType = getPhaseTypeForStep(step.id);
      const phase = phaseType ? actualPhaseByType.get(phaseType) : null;
      const apiPhase = phaseType ? apiPhaseByType.get(phaseType) : null;
      const progressItem = derivedStepProgress.find(
        (item) => item.stepId === step.id,
      );
      const phaseStartMs = getLocalDateMs(phase?.startDate);
      const phaseEndMs = getLocalDateMs(phase?.endDate);
      const phaseIsActive =
        !!phaseStartMs &&
        phaseStartMs <= todayMs &&
        (!phaseEndMs || phaseEndMs >= todayMs);
      // A step whose progress the cycle decides takes no completion from the
      // phase it borrows. `outcome` shares the luteal phase's row, and that
      // phase's `endDate` is her pregnancy test date — so once the test date
      // passed, the strip ticked the Outcome tab off with nothing recorded.
      const takesCompletionFromPhase = !derivedProgressStepIds.has(step.id);
      const isComplete =
        progressItem?.status === "completed" ||
        progressItem?.status === "skipped" ||
        (takesCompletionFromPhase &&
          !!phaseEndMs &&
          phaseEndMs < todayMs &&
          step.id !== "trigger_shot" &&
          step.id !== "freezing");
      const isActive =
        step.id === currentStepId ||
        ((step.id === "protocol" || step.id === "stimulation") &&
          phaseIsActive);
      const status: CycleChartTimelineItem["status"] = isComplete
        ? "completed"
        : isActive
          ? "active"
          : "upcoming";
      /**
       * The outcome step borrows the luteal phase's row, so the ordinary path
       * would have it repeat that phase's dates word for word. It has better
       * dates of its own — the day a result came in, or the test she has
       * scheduled — and falls through to its own estimate when it has neither.
       */
      const outcomeTiming =
        step.id === "outcome"
          ? resolveOutcomeTiming({
              recordedOn: cycleOutcome?.dateRecorded,
              testDate: waitingTestDate,
            })
          : null;
      const actualTiming =
        step.id === "trigger_shot" || step.id === "freezing"
          ? null
          : outcomeTiming
            ? outcomeTiming.kind === "recorded"
              ? t("care.cycle_detail.timeline.result_recorded_on", {
                  params: {
                    date: formatTimelineDate(
                      getLocalDateMs(outcomeTiming.date) ?? todayMs,
                      locale,
                    ),
                  },
                })
              : outcomeTiming.kind === "test"
                ? t("care.cycle_detail.timeline.test_on", {
                    params: {
                      date: formatTimelineDate(
                        getLocalDateMs(outcomeTiming.date) ?? todayMs,
                        locale,
                      ),
                    },
                  })
                : null
            : formatPhaseTiming(phase, status, todayStr, t, locale);
      const estimatedWindow = estimateTimelineWindow({
        stepId: step.id,
        cycleType,
        cycleStartMs,
      });
      // Show the estimated window as real calendar dates ("Est. Jun 5 – Jun 15")
      // so patients can plan around them. The "Est." prefix keeps it read as a
      // rough textbook-average estimate rather than a firm commitment.
      const estimatedTiming = t("care.cycle_detail.timeline.est_window", {
        params: {
          window: formatTimelineWindow(
            estimatedWindow.startMs,
            estimatedWindow.endMs,
            locale,
          ),
        },
      });

      return {
        stepId: step.id,
        timing:
          actualTiming ??
          // The outcome step goes straight to its own estimate. Both fallbacks
          // below read the luteal phase it borrows — its progress row and its
          // API timing string — so either would put the luteal phase's dates
          // back on the tab that `resolveOutcomeTiming` just kept them off.
          (outcomeTiming
            ? estimatedTiming
            : isComplete && progressItem?.completedAt
              ? t("care.cycle_detail.timeline.filled", {
                  params: {
                    date: formatTimelineDate(
                      getLocalDateMs(progressItem.completedAt) ?? todayMs,
                      locale,
                    ),
                  },
                })
              : apiPhase?.timing && status !== "upcoming"
                ? apiPhase.timing
                : estimatedTiming),
        status,
      };
    });
  }, [
    activeCycle.phases,
    apiJourneyPhases,
    cycle?.startDate,
    cycleOutcome?.dateRecorded,
    cycleType,
    currentStepId,
    derivedStepProgress,
    steps,
    todayStr,
    waitingTestDate,
    t,
    locale,
  ]);

  const journeyTimingByStepId = useMemo(
    () =>
      Object.fromEntries(
        cycleChartTimeline.map((item) => [item.stepId, item.timing]),
      ) as Record<string, string>,
    [cycleChartTimeline],
  );

  // Clinically-derived per-step status (from the real phase dates) so the hub's
  // "you are here" pointer follows the cycle stage, not what's been logged.
  const journeyStepStatusById = useMemo(() => {
    const map = Object.fromEntries(
      cycleChartTimeline.map((item) => [item.stepId, item.status]),
    ) as Record<string, CycleChartTimelineItem["status"]>;
    // The Outcome step is where the patient is for both the two-week wait and a
    // recorded-but-unclosed result, so the hub stays on that tab across the whole
    // ending rather than jumping off it the moment a result lands.
    //
    // This used to point at `luteal_phase`, because the wait was rendered on that
    // tab for want of one of its own. It now has one, and the luteal step keeps
    // whatever status its own phase dates give it — she can still open it and see
    // the progesterone she is taking.
    if (isOnOutcomeStep) map["outcome"] = "active";
    return map;
  }, [cycleChartTimeline, isOnOutcomeStep]);

  /**
   * Save where the pregnancy stands.
   *
   * Goes through the same write the beta uses, so it supersedes the record
   * already on the cycle instead of adding a second one — two current records
   * make the API's singular `outcome` resolve to null and every read surface
   * stops seeing any result. `statePregnancyStatus` decides what her answer
   * states; the write layer decides how it reaches the API and what it preserves.
   *
   * The cycle is deliberately not closed here. Closing stays its own act, and the
   * end-cycle flow already keeps a recorded loss addressable when she gets there.
   */
  const updatePregnancyFollowUp = useUpdateCyclePregnancyFollowUp();
  const pregnancyFollowUp = useCyclePregnancyFollowUp(cycle?.id ?? null);

  /**
   * The recap that follows a first announcement.
   *
   * The arrival carousel's slides: what happened, that nothing needs deciding
   * today, and where things stand. They lived only on Home, which is not where she
   * says the pregnancy is going — that happens here, on the cycle's outcome step —
   * so the moment passed with nothing said back to her.
   */
  const [pregnancyRecapVisible, setPregnancyRecapVisible] = useState(false);
  /**
   * Queued rather than opened directly.
   *
   * Both of these are `Modal`s, and dismissing one while presenting another in
   * the same frame is the case iOS drops: the update sheet goes, and nothing
   * takes its place. So the save asks for the window, and the effect below opens
   * it once the sheet it replaces is actually gone.
   */
  const [pregnancyRecapPending, setPregnancyRecapPending] = useState(false);
  /**
   * The weeks she gave with the announcement, if she gave any.
   *
   * Read from what she just said rather than from the record: the write has
   * returned, but the cycle query has not refetched yet, so `cycleOutcome` at
   * this moment is still the version without them.
   */
  const [pregnancyRecapWeeks, setPregnancyRecapWeeks] = useState<number | null>(
    null,
  );
  /** Same bound the pregnancy-follow-up dialog uses, and for the same reason. */
  const recapMaxHeight = Math.min(760, windowHeight * 0.9);

  useEffect(() => {
    if (!pregnancyRecapPending || updatePregnancyVisible) return;
    const frame = requestAnimationFrame(() => {
      setPregnancyRecapPending(false);
      setPregnancyRecapVisible(true);
    });
    return () => cancelAnimationFrame(frame);
  }, [pregnancyRecapPending, updatePregnancyVisible]);

  /**
   * The dates the window's steps carry, when there are any.
   *
   * Home dates the same cards from her reported weeks, so a window opened the
   * moment she reports them must not read "in the coming weeks" while the page
   * behind it names the day. Same pipeline, same gate: weeks she gave, counted
   * from today because today is when she said it.
   *
   * `pregnancyStands: "continuing"` is what she just told us, and it is the whole
   * permission `mayShowGestationalAge` asks for — no claim here about the
   * follow-up write, which is fired without being awaited.
   */
  const pregnancyRecapDating = useMemo(() => {
    const today = toLocalDateString(new Date());
    return buildEarlyPregnancyDating({
      t,
      locale,
      announcement: { pregnancyStands: "continuing" },
      gestation: gestationFromReportedWeeks({
        weekCount: pregnancyRecapWeeks,
        reportedOn: today,
        today,
      }),
      preciseAge: null,
      resultDate: cycleOutcome?.dateRecorded ?? null,
      today,
    });
  }, [cycleOutcome?.dateRecorded, locale, pregnancyRecapWeeks, t]);

  /**
   * The cards that recap a first announcement.
   *
   * `buildEarlyPregnancyWhatToKnowCards` is the set Home shows under "Bon à
   * savoir": what usually comes next with its steps, what a beta value can and
   * cannot say, and when not to wait for the next appointment. Dated when she
   * gave a week, undated when she did not — the cards carry both wordings.
   */
  const pregnancyRecapCards = useMemo(
    () =>
      buildEarlyPregnancyWhatToKnowCards(
        t,
        getPostProcedureOutcomeContext(cycle?.cycleType, "verified-positive")
          .contentKey,
        pregnancyRecapDating,
      ),
    [cycle?.cycleType, pregnancyRecapDating, t],
  );

  /**
   * Tells the server the pregnancy is being followed, once she has said there is
   * one.
   *
   * Her word is what starts this — the same evidence `summarisePregnancyStatus`
   * already requires, not the positive beta, which the app wrote rather than her.
   * A birth or a loss closes the follow-up instead; the outcome record carries
   * what happened, and the follow-up only needs to stop.
   *
   * Deliberately not awaited by the caller and never surfaced as an error. The
   * outcome she just recorded is the clinical write and it has already
   * succeeded; telling her "that failed" because a secondary record did not
   * would be false. The only consequence of a failure here is that no dates
   * appear, which is the same as the state she was already in — so it is logged
   * for us rather than raised at her.
   */
  const recordPregnancyFollowUp = useCallback(
    async (status: PregnancyStatus) => {
      const outcomeId = cycleOutcome?.id;
      if (!cycle?.id || !outcomeId) return;

      const existingFollowUp = pregnancyFollowUp.data;
      const isOngoing = status.stands === "continuing";
      try {
        await updatePregnancyFollowUp.mutateAsync({
          cycleId: cycle.id,
          body: {
            outcomeId,
            status: isOngoing ? "opted_in" : "ended",
            // This app claims no dating of its own: a transfer anchor is only
            // usable with the embryo culture day — the API says as much,
            // "without it transfer dating derives no gestational age rather than
            // assuming a day" — and that day is recorded nowhere here. Hers is
            // the dating that works, from the weeks she reports.
            //
            // But the PUT replaces the record, so writing nulls would erase an
            // anchor someone else had set — a clinic-entered due date, a later
            // surface, anything this screen knows nothing about. Whatever is
            // already there is carried through untouched.
            datingMethod: existingFollowUp?.datingMethod ?? "unknown",
            datingAnchorDate: existingFollowUp?.datingAnchorDate ?? null,
            datingAnchorPrecision:
              existingFollowUp?.datingAnchorPrecision ?? null,
            transferEmbryoDay: existingFollowUp?.transferEmbryoDay ?? null,
            endedReason: null,
          },
        });
      } catch (error) {
        console.error(
          "[CycleDetailScreen] Could not record the pregnancy follow-up",
          error,
        );
      }
    },
    [
      cycle?.id,
      cycleOutcome?.id,
      pregnancyFollowUp.data,
      updatePregnancyFollowUp,
    ],
  );

  const handleSavePregnancyStatus = useCallback(
    async (status: PregnancyStatus) => {
      if (!cycle?.id) return;
      setUpdatePregnancyError(null);
      try {
        const requestId = resolveOutcomeWriteRequestId(
          updatePregnancyRequestIdRef.current,
          outcomeWriteTarget({ outcomeId: cycleOutcome?.id ?? null }),
          createIdempotencyKey,
        );
        updatePregnancyRequestIdRef.current = requestId;
        await recordCycleOutcome.mutateAsync({
          cycleId: cycle.id,
          outcomeId: cycleOutcome?.id ?? null,
          clientRequestId: requestId.requestId,
          existingNotes: cycleOutcome?.notes ?? null,
          existingDateRecorded: cycleOutcome?.dateRecorded ?? null,
          // Keep the date the result came in. This adds detail to a result already
          // recorded; it is not a new one, so "recorded on" must not move to today.
          recordedOn: cycleOutcome?.dateRecorded ?? null,
          ...statePregnancyStatus(status),
        });
        updatePregnancyRequestIdRef.current = null;
        // Read before the write: it is the write that makes it `opted_in`, and
        // this is the one moment worth marking — the first time she tells us the
        // pregnancy is going. Every later correction, a week added or changed,
        // leaves it already opted in and opens nothing, because a recap she has
        // to dismiss on each edit is a door, not a welcome.
        // Never announced before, rather than not announced right now. Two ways
        // the looser test misfired: an unread or failed follow-up leaves the
        // status undefined, so the recap reopened on every later week edit; and a
        // pregnancy recorded as ended writes `"ended"`, so correcting a mis-tapped
        // loss back to continuing was met with "Congratulations" a second time.
        const followUpStatus = pregnancyFollowUp.data?.status;
        const isFirstAnnouncement =
          status.stands === "continuing" &&
          (followUpStatus === "not_configured" ||
            followUpStatus === "dismissed");
        void recordPregnancyFollowUp(status);
        setUpdatePregnancyVisible(false);
        // No scrolling: it opens over whatever she was reading, which is the
        // point of it being a window rather than a section of this page.
        if (isFirstAnnouncement) {
          setPregnancyRecapWeeks(
            status.stands === "continuing" ? (status.weekCount ?? null) : null,
          );
          setPregnancyRecapPending(true);
        }
      } catch (error) {
        // Logged as well as mapped. The mapper's fallback is "something went
        // wrong, try again", which is the right thing to show her and tells us
        // nothing — a failure that reaches it is by definition one we did not
        // anticipate, and it is the one worth being able to read.
        console.error(
          "[CycleDetailScreen] handleSavePregnancyStatus failed",
          error,
        );
        // Through the shared mapper, never `error.message`. A raw API string in
        // front of a patient is not an explanation, and this write raises the same
        // `CycleOutcomeWriteError` the beta does — conflict, a record that is not
        // hers, one her clinic authored — each of which has copy already.
        setUpdatePregnancyError(
          betaResultErrorMessage(
            error,
            t,
            {
              isSupporting: perspective === "partner",
              cycleOwnerName: partnerDisplayName,
            },
            cycleOutcome,
          ),
        );
      }
    },
    [
      recordPregnancyFollowUp,
      cycle?.id,
      cycleOutcome,
      partnerDisplayName,
      perspective,
      pregnancyFollowUp.data?.status,
      recordCycleOutcome,
      t,
    ],
  );

  if (activeCycle.isLoading) {
    return (
      <GlassScreenLayout>
        <View style={{ flex: 1 }} />
      </GlassScreenLayout>
    );
  }

  if (!activeCycle.cycle) {
    return (
      <GlassScreenLayout>
        {lastClosedOutcome ? (
          <PostCycleOrEmptyState
            lastOutcome={lastClosedOutcome}
            onBack={handleBack}
            onBackToCare={() => {
              navigation.reset({
                index: 0,
                routes: [{ name: "CareOverview" }],
              });
            }}
            onOpenArticle={(articleId: string) => {
              const rootNav =
                navigation.getParent<NavigationProp<MainTabParamList>>();
              rootNav?.navigate("Learn", {
                screen: "ArticleDetail",
                params: { articleId, returnTab: "Care" },
              });
            }}
          />
        ) : (
          <NoActiveCycleState
            onBack={handleBack}
            onStartTreatment={handleOpenStartTreatment}
            onAddPastCycle={() => setAddPastCycleVisible(true)}
          />
        )}
        <MoonStartTreatmentModal
          visible={startTreatmentVisible}
          onClose={() => {
            setStartTreatmentVisible(false);
            setEditTreatmentData(null);
            setEditTreatmentInitialStep(1);
            setAllowTreatmentTypeBackNavigation(true);
          }}
          onStartTreatment={handleStartTreatment}
          isLoading={
            createCycle.isPending ||
            createEmbryoFreezingPlan.isPending ||
            acceptEmbryoFreezingPlan.isPending ||
            updateCycleMutation.isPending
          }
          goal={treatmentGoal}
          gender={
            patient?.gender === "AMAB"
              ? "male"
              : patient?.gender === "AFAB"
                ? "female"
                : null
          }
          editData={editTreatmentData ?? undefined}
          initialStep={editTreatmentInitialStep}
          allowTreatmentTypeBackNavigation={allowTreatmentTypeBackNavigation}
        />
        <AddPastCycleDialog
          visible={addPastCycleVisible}
          onClose={() => setAddPastCycleVisible(false)}
        />
      </GlassScreenLayout>
    );
  }

  /**
   * The two-week wait, for the cycle types that have no other surface for it.
   *
   * It renders as the terminal "Outcome" tab inside the hub (via
   * `renderStepPanelOverride`) rather than taking over the whole screen — and on
   * an FET cycle it no longer renders at all.
   *
   * Two surfaces were answering for the same tab. Before the transfer was
   * confirmed the tab fell through to the step's own panel: the result card, how
   * it works, what to expect, and the way back to where she left off. The moment
   * the transfer landed, `isInWaitingPhase` turned true and this view took the
   * tab over with a different design — a status line, a ring asking her to pick
   * a date, and its own pair of buttons. Same tab, same cycle, same wait, two
   * looks, and the switch happened on a fact that has nothing to do with how the
   * wait should look.
   *
   * The step panel is the one that stays. Nothing is lost by it: recording the
   * beta from there opens the very same modal this view's button opened, and the
   * test date it asked her to pick was already read-only on FET
   * (`onTestDateChange` was undefined — that clinic's order is not hers to set).
   *
   * IVF, ICSI, IUI and OI keep this view: their outcome step has no equivalent
   * panel, so removing it there would leave them with no wait surface at all.
   */
  const waitingPhaseView =
    isInWaitingPhase && cycleType !== "fet" ? (
      <WaitingPhaseView
        lutealStartDate={waitingStartDate}
        testDate={waitingTestDate}
        onTestDateChange={handleSetTestDate}
        onRecordResult={() => {
          setBetaResultError(null);
          setBetaResultVisible(true);
        }}
        onEditTestAppointment={() => {
          setAppointmentPrefill({
            type: "exam",
            activities: ["Beta hCG"],
            title: "Beta hCG",
            date: waitingTestDate ?? "",
          });
          setAddAppointmentVisible(true);
        }}
        onChatAboutThis={() => {
          const rootNav =
            navigation.getParent<NavigationProp<MainTabParamList>>();
          rootNav?.navigate("Chat", {
            screen: "Assistant",
            params: {
              initialPrompt: t(
                "care.treatment_hub.waiting_phase.chat_seed_prompt",
              ),
              autoSendInitialPrompt: true,
            },
          });
        }}
        isSavingTestDate={isSavingTestDate}
        testDateSaveError={testDateSaveError}
        onRetryTestDate={
          pendingTestDate
            ? () => void handleSetTestDate(pendingTestDate)
            : undefined
        }
        isLoadingResult={recordCycleOutcome.isPending}
        perspective={perspective}
        partnerDisplayName={partnerDisplayName}
      />
    ) : null;

  /**
   * The state between recording an ending and closing the cycle.
   *
   * `isInWaitingPhase` goes false as soon as an outcome type exists, which used
   * to coincide with the cycle closing in the same PATCH. Now that recording
   * leaves the cycle open, this panel is what stands in that gap — otherwise the
   * wait view would simply vanish with nothing in its place.
   */
  const recordedResultView =
    hasRecordedOpenResult && cycleOutcome?.type ? (
      <RecordedResultView
        kind={cycleOutcome.type}
        recordedDate={
          cycleOutcome.dateRecorded
            ? formatDate(
                new Date(`${cycleOutcome.dateRecorded}T00:00:00`),
                locale,
                { day: "numeric", month: "long", year: "numeric" },
              )
            : null
        }
        // Shown to her and to a partner alike — the summary is what may be seen
        // either way, and it never carries the kind of loss.
        pregnancyStatus={
          cycleOutcome
            ? summarisePregnancyStatus({
                ...cycleOutcome,
                // Her word, when she gave no week: the update step writes
                // `opted_in` from the same answer. Read here so the card can say
                // "pregnant" without inventing a number.
                //
                // Only when the follow-up was written against this very outcome.
                // Correcting the beta to a negative leaves the old follow-up
                // opted in, and without this the card printed "Pregnant" directly
                // under "This cycle didn't end in a pregnancy".
                followUpStatus:
                  pregnancyFollowUp.data?.outcomeId === cycleOutcome.id
                    ? (pregnancyFollowUp.data?.status ?? null)
                    : null,
              })
            : null
        }
        onCloseCycle={handleCloseRecordedCycle}
        onUpdateResult={() => {
          setBetaResultError(null);
          setBetaResultVisible(true);
        }}
        // Only the person who carried the cycle. Reporting a loss is a fact
        // about her body, not a partner's to enter.
        onUpdatePregnancy={
          isPartnerCycle
            ? undefined
            : () => {
                setUpdatePregnancyError(null);
                setUpdatePregnancyVisible(true);
              }
        }
        // Same perspective the wait view on this step uses, so the copy does not
        // switch voice the moment a result is recorded.
        perspective={perspective}
        isClosing={createSnapshot.isPending}
      />
    ) : null;

  return (
    <GlassScreenLayout edges={["bottom", "left", "right"]}>
      <ScrollView
        ref={scrollRef}
        contentContainerStyle={{
          paddingBottom: 100,
          maxWidth: 520,
          width: "100%",
          alignSelf: "center",
        }}
        showsVerticalScrollIndicator={false}
      >
        {/* ── HERO HEADER ── */}
        <View
          style={{
            paddingTop: insets.top + 12,
            paddingHorizontal: 20,
            paddingBottom: 28,
            borderBottomLeftRadius: 28,
            borderBottomRightRadius: 28,
            overflow: "hidden",
          }}
        >
          {/* Bronze-gradient background */}
          <Image
            source={require("../../assets/images/bronze-gradient.webp")}
            style={[StyleSheet.absoluteFill, { width: "100%", height: "100%" }]}
            resizeMode="cover"
          />

          {/* Decorative illustration — top-right */}
          <View
            pointerEvents="none"
            style={{
              position: "absolute",
              top: insets.top + 16,
              right: 0,
            }}
          >
            <CycleIllustration cycleType={cycle?.cycleType} />
          </View>

          {/* Back */}
          <Pressable
            accessibilityRole="button"
            accessibilityLabel={t("care.overview.screen_title")}
            onPress={() => {
              // Arrow-only control; its accessibility label is the copy.
              capturePress(
                pressLabel(t("care.overview.screen_title")),
                "back_to_care",
              );
              handleBack();
            }}
            style={{
              width: 36,
              height: 36,
              borderRadius: 18,
              backgroundColor: "rgba(255,255,255,0.08)",
              alignItems: "center",
              justifyContent: "center",
              marginBottom: 20,
            }}
          >
            <ArrowLeft size={18} color="rgba(255,255,255,0.6)" />
          </Pressable>

          {/* Eyebrow */}
          <View
            style={{
              flexDirection: "row",
              alignItems: "center",
              flexWrap: "wrap",
              gap: 8,
              alignSelf: "flex-start",
              marginBottom: 6,
            }}
          >
            <View
              style={{
                flexDirection: "row",
                alignItems: "center",
                gap: 6,
              }}
            >
              <Activity size={13} color={WARM} strokeWidth={1.5} />
              <Text
                className="font-dm-sans"
                style={{
                  fontSize: 11,
                  fontWeight: "700",
                  color: WARM,
                  letterSpacing: 0.5,
                  textTransform: "uppercase",
                }}
              >
                {t("care.cycle_detail.empty.active_cycle_eyebrow")}
              </Text>
            </View>
            {cycle && (
              <View
                style={{
                  backgroundColor: `${WARM}25`,
                  paddingHorizontal: 10,
                  paddingVertical: 5,
                  borderRadius: 8,
                }}
              >
                <Text
                  className="font-dm-sans"
                  style={{ fontSize: 12, fontWeight: "600", color: WARM }}
                >
                  {currentPhaseLabel ??
                    t("care.cycle_detail.empty.starting_soon")}
                </Text>
              </View>
            )}
          </View>

          {/* Title row */}
          <View
            style={{ flexDirection: "row", alignItems: "flex-start", gap: 8 }}
          >
            <Text
              className="font-dm-sans"
              style={{
                flex: 1,
                fontSize: 22,
                fontWeight: "700",
                color: WARM_LIGHT,
                letterSpacing: -0.3,
                lineHeight: 27,
              }}
            >
              {cycle
                ? `${displayName} #${cycleNumber}`
                : t("care.cycle_detail.empty.no_active_cycle_main")}
            </Text>
            {cycle && (
              <View style={{ marginTop: -4 }}>
                <KebabMenu
                  buttonColor="rgba(255,255,255,0.86)"
                  items={heroMenuItems}
                  accessibilityLabel="Cycle actions"
                />
              </View>
            )}
          </View>

          {/* Metadata chips */}
          {cycle && (
            <View
              style={{
                flexDirection: "row",
                flexWrap: "wrap",
                gap: 8,
                marginTop: 16,
              }}
            >
              {cycle.provider ? (
                <View
                  style={{
                    backgroundColor: "rgba(255,255,255,0.06)",
                    paddingHorizontal: 10,
                    paddingVertical: 5,
                    borderRadius: 8,
                    borderWidth: 1,
                    borderColor: "rgba(255,255,255,0.12)",
                  }}
                >
                  <Text
                    className="font-dm-sans"
                    style={{
                      fontSize: 12,
                      fontWeight: "500",
                      color: "rgba(255,255,255,0.75)",
                    }}
                  >
                    {cycle.provider}
                  </Text>
                </View>
              ) : (
                <Pressable
                  onPress={() => {
                    capturePress(
                      pressLabel(t("care.cycle_detail.buttons.add_clinic")),
                      "care.cycle_detail.buttons.add_clinic",
                    );
                    handleEditTreatmentWhereWhen();
                  }}
                  style={({ pressed }) => ({
                    flexDirection: "row",
                    alignItems: "center",
                    gap: 4,
                    backgroundColor: pressed
                      ? "rgba(255,255,255,0.08)"
                      : "transparent",
                    paddingHorizontal: 10,
                    paddingVertical: 5,
                    borderRadius: 8,
                    borderWidth: 1,
                    borderStyle: "dashed",
                    borderColor: "rgba(255,255,255,0.22)",
                  })}
                >
                  <Plus
                    size={11}
                    color="rgba(255,255,255,0.7)"
                    strokeWidth={2}
                  />
                  <Text
                    className="font-dm-sans"
                    style={{
                      fontSize: 12,
                      fontWeight: "500",
                      color: "rgba(255,255,255,0.7)",
                    }}
                  >
                    {t("care.cycle_detail.buttons.add_clinic")}
                  </Text>
                </Pressable>
              )}
              {cycle.startDate ? (
                <View
                  style={{
                    backgroundColor: "rgba(255,255,255,0.06)",
                    paddingHorizontal: 10,
                    paddingVertical: 5,
                    borderRadius: 8,
                    borderWidth: 1,
                    borderColor: "rgba(255,255,255,0.12)",
                  }}
                >
                  <Text
                    className="font-dm-sans"
                    style={{
                      fontSize: 12,
                      fontWeight: "500",
                      color: "rgba(255,255,255,0.75)",
                    }}
                  >
                    {t("care.cycle_detail.cycle_label.started_chip", {
                      params: { date: cycleStartFormatted ?? "" },
                    })}
                  </Text>
                </View>
              ) : (
                <Pressable
                  onPress={() => {
                    capturePress(
                      pressLabel("Add start date"),
                      "add_cycle_start_date",
                    );
                    handleEditTreatmentWhereWhen();
                  }}
                  style={({ pressed }) => ({
                    flexDirection: "row",
                    alignItems: "center",
                    gap: 4,
                    backgroundColor: pressed
                      ? "rgba(255,255,255,0.08)"
                      : "transparent",
                    paddingHorizontal: 10,
                    paddingVertical: 5,
                    borderRadius: 8,
                    borderWidth: 1,
                    borderStyle: "dashed",
                    borderColor: "rgba(255,255,255,0.22)",
                  })}
                >
                  <Plus
                    size={11}
                    color="rgba(255,255,255,0.7)"
                    strokeWidth={2}
                  />
                  <Text
                    className="font-dm-sans"
                    style={{
                      fontSize: 12,
                      fontWeight: "500",
                      color: "rgba(255,255,255,0.7)",
                    }}
                  >
                    Add start date
                  </Text>
                </Pressable>
              )}
            </View>
          )}
        </View>

        {/* ── BODY ── */}
        <View
          // The hub opens this section, so where the body starts is where the hub
          // starts. An arrival that asked for a hub step scrolls here: this screen
          // stays mounted between visits, so navigating to it again would
          // otherwise land wherever it was left — for a patient sent to read her
          // result, the bottom of a long page.
          onLayout={(event) => {
            const { y } = event.nativeEvent.layout;
            setHubOffsetY((previous) => (previous === y ? previous : y));
          }}
          style={{ paddingHorizontal: 16, paddingTop: 8 }}
        >
          {/* ━━ TREATMENT HUB (the two-week wait renders as the terminal
              "Outcome" tab via renderStepPanelOverride, not a full takeover) ━━ */}
          <MoonTreatmentHubCard
            ref={treatmentHubRef}
            activeCycle={hubCycle}
            isCycleLoading={activeCycle.isLoading}
            displayTreatmentName={displayName}
            isReadOnly={isPartnerCycle}
            onEditTreatment={handleEditTreatment}
            onUpdateStepProgress={handleUpdateStepProgress}
            onOpenMedicationModal={handleOpenMedicationModal}
            hasSpecimens={totalEggs + totalEmbryos + totalSperm > 0}
            /**
             * Entering the two-week wait is not an ending — it adds the luteal
             * phase and the beta date, and the button says so. Everything else
             * here closes the cycle, and closing always goes through the dialog
             * that asks.
             *
             * Routing non-preservation cycle types straight at
             * `handleCompletePreservation` closed them on one tap: with
             * `treatmentGoal === "pregnancy"` and no confirmed transfer it
             * PATCHes `status: "closed"`, today's `endDate` and a null outcome,
             * then resets to Care overview. The button sits on the journey's
             * last step, which for ivf/icsi/iui/fet/oi is the appended
             * `outcome` tab — so a patient on day 6 of stimulation who opened
             * that tab to see what was coming could end her cycle with one
             * press and no way back.
             */
            onFinishTreatment={() => {
              if (hasConfirmedTransfer) {
                handleCompletePreservation();
                return;
              }
              setEndCycleMode("complete");
              setEndCycleVisible(true);
            }}
            onStartTreatment={handleOpenStartTreatment}
            goal={treatmentGoal}
            canEnterWaiting={hasConfirmedTransfer}
            celebrateCompletion={mayCelebrateCompletion}
            treatmentIsDone={treatmentIsDone}
            cycleNumber={cycleNumber}
            onEditMedication={handleEditMedication}
            onAddSpecimen={() => {
              setAddSpecimenForRetrieval(false);
              setAddSpecimenVisible(true);
            }}
            onRecordProcedureOutcome={(stepId) => {
              // The Outcome tab's only way to record a result used to be the
              // two-week-wait card, which does not render until a luteal phase
              // row exists — so a patient who never logged luteal support had
              // no way to record her beta from the hub at all.
              if (stepId === "outcome") {
                setBetaResultError(null);
                setBetaResultVisible(true);
                return;
              }
              if (cycle?.cycleType === "egg_freezing") {
                setAddSpecimenForRetrieval(true);
                setAddSpecimenVisible(true);
              }
            }}
            onScheduleProcedure={(stepId) => {
              const prefill = getProcedureAppointmentPrefill(stepId);
              if (!prefill) return;
              setAppointmentPrefill({
                ...prefill,
                cycleId: cycle?.id ?? null,
              });
              setAddAppointmentVisible(true);
            }}
            onAddResults={() =>
              openCurrentCycleDocuments(true, "medical_records")
            }
            usingFrozenSpecimens={usingFrozenSpecimens}
            stepTimingById={journeyTimingByStepId}
            clinicalStepStatusById={journeyStepStatusById}
            clinicRecordedStepIds={clinicRecordedStepIds}
            renderCalendar={() => (
              <CalendarOverviewCard
                cycleId={cycle?.id ?? null}
                onOpenMedicationFactSheet={setCalendarMedicationSheet}
                onOpenAppointment={setCalendarAppointmentSheet}
                onOpenProcedureFactSheet={handleOpenCalendarProcedure}
                onLoggingOverlayChange={setCalendarSymptomOverlay}
                onAddMedication={() => setAddMedVisible(true)}
                onAddAppointment={() => setAddAppointmentVisible(true)}
              />
            )}
            onQuickAddMedication={() => setAddMedVisible(true)}
            onQuickAddAppointment={() => setAddAppointmentVisible(true)}
            onQuickAddTest={() =>
              openCurrentCycleDocuments(true, "medical_records")
            }
            requestedStepId={hubStepRequest}
            requestedStepArrival={hubStepRequestCount}
            /**
             * What the clinic filed against this cycle's beta order, on the
             * step she now watches it from.
             *
             * A single result reaches the panel through the recorded outcome,
             * but a series does not — and a rising or falling pair is the whole
             * clinical point of repeating the test. It is added to the step
             * rather than replacing it, so the record action and the guidance
             * stay where they are.
             *
             * Only once the lookup has answered: rendering it while the query
             * is still in flight says "no beta order" to a patient whose clinic
             * has filed one.
             */
            renderStepPanelExtra={(stepId) =>
              // `isFetched` alone was not enough on either side. It turns true
              // on a *failed* fetch as well, so a lookup that errored rendered
              // "your clinic has not reported a beta order" — asserting the
              // clinic filed nothing when the app had simply failed to look. And
              // with no transfer on record there is nothing for an order to hang
              // off, so on day one of every FET cycle, clinic connected or not,
              // the tab stated the same absence as a fact.
              stepId === "outcome" &&
              cycleType === "fet" &&
              fetBetaLifecycleQuery.isFetched &&
              !fetBetaLifecycleQuery.isError &&
              isReportedFETTransfer(fetBetaLifecycleQuery.data?.procedure) ? (
                <FETBetaResultSeries
                  lifecycle={fetBetaLifecycleQuery.data ?? null}
                />
              ) : null
            }
            renderStepPanelOverride={(stepId) =>
              stepId === "outcome"
                ? // The wait and the recorded-but-unclosed result are successive
                  // states of the same step, so they share its panel: the wait
                  // gives way to the result the moment one is recorded. Luteal
                  // support is its own tab now and keeps its own panel.
                  (waitingPhaseView ?? recordedResultView ?? undefined)
                : undefined
            }
          />

          {/* ━━ CRYO VAULT SECTION (preservation cycles) ━━ */}
          {treatmentGoal === "preserve" && currentStepId === null && (
            <>
              <CycleSectionTitle
                title={t("care.cycle_detail.sections.cryo_vault")}
              />
              <CryoVaultOverviewCard
                onPress={() => navigation.navigate("CryoVault")}
                onAddSpecimen={() => setAddSpecimenVisible(true)}
                onNoSpecimens={() => setAddSpecimenVisible(true)}
                onPressSpecimen={(specimen) =>
                  setSelectedSpecimenId(specimen.specimenIds[0] ?? null)
                }
                specimens={bridgeSpecimens}
              />
            </>
          )}

          <CycleTestsSection
            testResults={currentCycleTestResults}
            cycleStartDate={cycle?.startDate ?? null}
            phases={phases}
            cycleLink={{
              label: currentCycleDocumentLabel,
              cycleId: cycle?.id,
            }}
            onOpenCycle={(cycleLink) => {
              navigation.navigate("CycleDetail", {
                cycleId: cycleLink.cycleId ?? cycle?.id,
              });
            }}
            onViewAll={() =>
              navigation.navigate("AllLabResults", {
                cycleId: cycle?.id,
                cycleLabel: currentCycleDocumentLabel,
              })
            }
            onAddTestResult={() =>
              openCurrentCycleDocuments(true, "medical_records")
            }
          />

          {/* ━━ PROTOCOL ━━ */}
          <CycleProtocolSection
            cycle={currentCycleProtocolData}
            onEditMedication={handleEditMedication}
            onAddMedication={() => setAddMedVisible(true)}
          />

          {/* ━━ MONITORING ━━ */}
          {/* Only for a cycle type that is actually monitored. This rendered
              unconditionally, which put a follicle-and-lining section on a
              sperm freeze, where there is nothing about the patient's cycle to
              follow — a section that could only ever show its empty state. */}
          <View>
            {isMonitoredCycle && (
              <OvarianStimulationMonitoringSection
                cycleId={cycle?.id}
                cycleType={cycle?.cycleType}
                cycleStartDate={cycle?.startDate}
                openEntryRequested={route.params?.focus === "monitoring"}
                onEntryRequestHandled={() => {
                  // Consume the arrival so pressing "add monitoring data" again
                  // re-opens the form. React Navigation reuses this screen when
                  // it is already the focused route and only swaps its params, so
                  // an uncleared `focus` makes every later request a no-op.
                  if (route.params?.focus) {
                    navigation.setParams({ focus: undefined });
                  }
                }}
                cycleLabel={currentCycleDocumentLabel}
                onUploadReport={(
                  dateISO,
                  onMonitoringExtracted,
                  onDocumentSaved,
                ) => {
                  void pickAndOpenDocumentUpload({
                    defaultCategory: "medical_records",
                    defaultTags: [
                      ...(cycle?.id ? [getCycleDocumentTag(cycle.id)] : []),
                      "monitoring-report",
                      `monitoring-date:${dateISO}`,
                    ],
                    onMonitoringExtracted,
                    onSave: onDocumentSaved,
                  });
                }}
                onOpenCycle={(cycleLink) => {
                  navigation.navigate("CycleDetail", {
                    cycleId: cycleLink.cycleId ?? cycle?.id,
                  });
                }}
                phases={hubPhases}
                derivedStepProgress={derivedStepProgress}
                defaultView="table"
              />
            )}
          </View>

          <CycleRelatedDocumentsSection
            documents={currentCycleDocuments}
            onViewAll={() => openCurrentCycleDocuments(false)}
            onUpload={() => openCurrentCycleDocuments(true, "medical_records")}
          />

          <View
            style={{
              marginTop: 28,
              marginBottom: 10,
              marginLeft: 4,
            }}
          >
            <SectionTitle icon={HeartHandshake}>
              {t("care.cycle_detail.sections.connect_care_team")}
            </SectionTitle>
          </View>
          <CycleClinicTeamCard
            members={currentCycleTeamMembers}
            clinicName={cycle?.provider ?? null}
            // TODO(@Bassem, @Olivier): connect active-cycle provider/clinic
            // actions to backend-backed care-team linking so this section can
            // create and update cycle healthcare professionals directly.
            onEditTeam={handleCycleTeamAction}
            onAddProviderOrClinic={handleCycleTeamAction}
          />
        </View>
      </ScrollView>
      <MedicationFactsheetSheet
        item={calendarMedicationSheet?.item ?? null}
        fallbackName={calendarMedicationSheet?.fallbackName}
        onClose={() => setCalendarMedicationSheet(null)}
        userContext={{
          ...calendarMedicationSheet?.userContext,
          source:
            apiMedications?.find(
              (p) => p.id === calendarMedicationSheet?.medicationId,
            )?.source ?? null,
        }}
        medicationId={calendarMedicationSheet?.medicationId}
        onEditPress={
          calendarMedicationSheet?.medicationId
            ? () => {
                const id = calendarMedicationSheet.medicationId;
                const name = calendarMedicationSheet.fallbackName;
                setCalendarMedicationSheet(null);
                if (id) handleEditMedicationById(id, name);
              }
            : undefined
        }
      />
      <AppointmentDetailSheet
        visible={calendarAppointmentSheet != null}
        appointment={calendarAppointmentSheet}
        onClose={() => setCalendarAppointmentSheet(null)}
        onEdit={(appointment) => {
          setCalendarAppointmentSheet(null);
          setEditAppointment(appointment);
        }}
        onOpenEducation={handleOpenCalendarAppointmentEducation}
      />
      <ProcedureDetailSheet
        item={calendarProcedureSheet?.item ?? null}
        appointment={
          calendarProcedureSheet &&
          !calendarProcedureEducationOnly &&
          calendarProcedureSheet.appointment.appointmentId
            ? {
                type: "appointment",
                id: calendarProcedureSheet.appointment.id,
                appointmentId: calendarProcedureSheet.appointment.appointmentId,
                title: calendarProcedureSheet.appointment.title,
                subtitle: calendarProcedureSheet.appointment.subtitle,
                time: calendarProcedureSheet.appointment.time,
                date: calendarProcedureSheet.appointment.date,
                ownership: (calendarProcedureSheet.appointment.ownership ??
                  undefined) as "patient" | "provider" | undefined,
                ownerId:
                  calendarProcedureSheet.appointment.ownerId ?? undefined,
                icon: CalendarClock,
                factsheetId: calendarProcedureSheet.item.id,
              }
            : undefined
        }
        onClose={() => {
          setCalendarProcedureSheet(null);
          setCalendarProcedureEducationOnly(false);
        }}
      />
      <MoonStartTreatmentModal
        visible={startTreatmentVisible}
        onClose={() => {
          setStartTreatmentVisible(false);
          setEditTreatmentData(null);
          setEditTreatmentInitialStep(1);
          setAllowTreatmentTypeBackNavigation(true);
        }}
        onStartTreatment={handleStartTreatment}
        isLoading={
          createCycle.isPending ||
          createEmbryoFreezingPlan.isPending ||
          acceptEmbryoFreezingPlan.isPending ||
          updateCycleMutation.isPending
        }
        goal={treatmentGoal}
        gender={
          patient?.gender === "AMAB"
            ? "male"
            : patient?.gender === "AFAB"
              ? "female"
              : null
        }
        editData={editTreatmentData ?? undefined}
        initialStep={editTreatmentInitialStep}
        allowTreatmentTypeBackNavigation={allowTreatmentTypeBackNavigation}
      />
      <CycleCareTeamModal
        visible={careTeamModalVisible}
        cycleId={cycle?.id}
        clinicName={cycle?.provider ?? null}
        members={cycleCareTeamMembers ?? []}
        onClose={() => setCareTeamModalVisible(false)}
        onOpenCareTeam={openPatientCareTeam}
      />
      <AddAppointmentModal
        visible={addAppointmentVisible || editAppointment != null}
        onClose={() => {
          setAddAppointmentVisible(false);
          setEditAppointment(null);
          setAppointmentPrefill(null);
          // Defensive refetch — `useCreateAppointment` already
          // invalidates these keys on success, but the snapshot tile's
          // "Schedule visit" → date flip wasn't always landing after the
          // modal closed (UAT issue #33 follow-up). Re-invalidating
          // here guarantees the next render sees the just-saved
          // appointment without waiting for the prefix-match to fire.
          queryClient.invalidateQueries({
            queryKey: appointmentKeys.patient(tenantId ?? "", patientId ?? ""),
          });
          queryClient.invalidateQueries({
            queryKey: calendarKeys.patient(tenantId ?? "", patientId ?? ""),
          });
        }}
        editAppointment={editAppointment}
        prefill={appointmentPrefill}
        onSaved={({
          appointmentId,
          startDate,
          startTime,
          consultationType,
        }) => {
          // Keep the two-week-wait card in sync: saving a beta hCG appointment
          // moves the pregnancy-test date (luteal-phase end date) that drives
          // the countdown, so the appointment and the ring never disagree.
          const isBetaTest = (consultationType ?? []).some(
            (activity) => activity.trim().toLowerCase() === "beta hcg",
          );
          if (isBetaTest && startDate) {
            handleSetTestDate(startDate);
          }
          // Scheduling a procedure appointment (egg retrieval, transfer, IUI,
          // sperm collection) mirrors the chosen date onto the matching step's
          // cycle phase so the treatment-hub step flips to "scheduled" — the
          // same date→phase mechanism the beta-hCG card uses above. The saved
          // appointment ID is also persisted on the procedure when available;
          // the date mirror keeps the hub responsive in the current client
          // state.
          const scheduled = findScheduledProcedureStep(consultationType);
          if (scheduled && startDate) {
            // Only carry a time if the appointment actually has one — a
            // date-only value round-trips through the phase without a
            // startTime, so the step shows no fabricated hour.
            const dateValue = startTime
              ? `${startDate}T${startTime}:00`
              : startDate;
            const optimisticData: Record<
              string,
              string | number | boolean | null
            > = {
              [scheduled.dateField]: dateValue,
              ...(appointmentId ? { appointmentId } : {}),
            };
            // Push "scheduled" into the hub's optimistic state before the
            // phase round-trip lands, otherwise the schedule row keeps
            // shadowing the just-scheduled state during the refetch window.
            treatmentHubRef.current?.applyScheduledOptimistic(
              scheduled.stepId,
              optimisticData,
            );
            // Explicitly persist as "scheduled" rather than "completed":
            // saving an appointment records the intent, not the outcome.
            // The old flow relied on `isFutureStart > today`, which quietly
            // flipped same-day scheduling to "completed" (endDate=nextDay)
            // and auto-advanced the journey to fertilization before the
            // patient could log any oocyte counts.
            // The appointment is already saved, by the modal, exactly as she
            // entered it. This call only mirrors its date onto the phase.
            void handleUpdateStepProgress(
              scheduled.stepId,
              { status: "scheduled", data: optimisticData },
              { skipCalendarSync: true },
            );
          }
        }}
      />
      <AddMedicationModal
        visible={addMedVisible || !!editMed}
        onClose={() => {
          setAddMedVisible(false);
          setHubMedCycleId(null);
          setHubMedPhaseId(null);
          setHubMedRole(null);
          setEditMed(null);
        }}
        cycleId={hubMedCycleId ?? cycle?.id ?? ""}
        phaseId={hubMedPhaseId ?? undefined}
        medicationRole={hubMedRole ?? undefined}
        editMedication={editMed}
      />
      {/* A window, after a first pregnancy is logged. Not a page section: this is
          a moment, and it should not become part of the cycle page she scrolls
          every day.

          The cards are the early-pregnancy "what to know" set — what usually comes
          next with its steps, how a beta is read, and when not to wait — in the
          same carousel Home gives them, so the two cannot drift.

          Bounded and scrollable, for the reason its sibling documents: an
          illustration, a header, an equalized card and a button add up past a
          small phone's viewport, and the dialog clips rather than scrolls, so the
          button went off the bottom with only the ✕ left to dismiss it. */}
      <Dialog
        visible={pregnancyRecapVisible}
        onClose={() => setPregnancyRecapVisible(false)}
        layout="centered"
        closeButton="minimal"
        testID="pregnancy-announcement-recap"
        containerStyle={{ maxWidth: 480, maxHeight: recapMaxHeight }}
        surfaceStyle={{ maxHeight: recapMaxHeight }}
      >
        <ScrollView
          showsVerticalScrollIndicator={false}
          contentContainerStyle={{ gap: 14, paddingBottom: 4 }}
          style={{ maxHeight: recapMaxHeight - 72 }}
        >
          {/* The app's own illustration, the one its pregnancy-follow-up dialog
              already wears — so a window about the weeks ahead is recognisably the
              same family, and the moment is not a wall of text. */}
          <DialogIcon
            source={PREGNANCY_RECAP_ILLUSTRATION}
            width={84}
            height={88}
          />
          {/* Its own words. The card behind it already says "A positive result ·
              Congratulations, your clinic will confirm what comes next"; repeating
              that here made her read the same two sentences twice, one over the
              other. What this window is for is the weeks ahead. */}
          <DialogHeader
            title={t(
              "homepage.review_concept.post_procedure_outcome.pregnancy_tracking.the_weeks_ahead",
            )}
            subtitle={t(
              "homepage.review_concept.post_procedure_outcome.pregnancy_tracking.what_usually_comes_next_and_who_to_ask",
            )}
          />
          <WhatToKnowView cards={pregnancyRecapCards} />
          <Text
            className="font-dm-sans"
            style={{
              fontSize: 12,
              lineHeight: 18,
              color: "#9A8E85",
              textAlign: "center",
            }}
          >
            {t(
              "homepage.review_concept.post_procedure_outcome.pregnancy_tracking.you_will_find_this_on_your_home_page",
            )}
          </Text>
          <DialogPrimaryButton
            label={t("homepage.cycle_closing.ui.done")}
            onPress={() => setPregnancyRecapVisible(false)}
          />
        </ScrollView>
      </Dialog>

      {/* Dating an upcoming collection of his sample, with no partner account to
          carry it. Framed as the one thing that is actually hers to do — send the
          invitation, once — because what follows it is automatic: a connected
          partner's calendar receives what concerns him without her passing
          anything on.

          Including this one. `computeAudience` does snapshot the audience at
          creation from the `appointments` sharing grant, and `updateAppointment`
          never recomputes it — but neither is the mechanism that matters:
          `audience-reconciliation.service.ts` maps that category onto the
          `appointments` table and appends the target to every row the patient
          owns, and both the sharing-preferences and partner services enqueue it.
          So a grant made after the booking reaches the booking. It lands on the same Connections
          screen the care-team entry point uses, rather than inventing a second
          invitation flow. */}
      <Dialog
        visible={partnerCalendarPromptVisible}
        onClose={() => setPartnerCalendarPromptVisible(false)}
        layout="centered"
        closeButton="minimal"
        testID="partner-calendar-prompt"
        containerStyle={{ maxWidth: 420 }}
      >
        <View style={{ gap: 14 }}>
          <DialogHeader
            title={t("care.treatment_hub.partner_calendar.invite_your_partner")}
            subtitle={t(
              "care.treatment_hub.partner_calendar.once_connected_and_shared_their_calendar_receives_them",
            )}
          />
          <DialogPrimaryButton
            label={t("care.treatment_hub.partner_calendar.invite_my_partner")}
            onPress={() => {
              setPartnerCalendarPromptVisible(false);
              openPatientCareTeam();
            }}
          />
          <Pressable
            onPress={() => setPartnerCalendarPromptVisible(false)}
            accessibilityRole="button"
            style={{ alignSelf: "center", paddingVertical: 6 }}
          >
            <Text
              className="font-dm-sans"
              style={{ fontSize: 13, color: "#9A8E85" }}
            >
              {t("care.treatment_hub.partner_calendar.not_now")}
            </Text>
          </Pressable>
        </View>
      </Dialog>

      <UpdatePregnancyModal
        visible={updatePregnancyVisible}
        onClose={() => setUpdatePregnancyVisible(false)}
        onSave={(status) => void handleSavePregnancyStatus(status)}
        isSaving={recordCycleOutcome.isPending}
        error={updatePregnancyError}
      />

      <EndCycleModal
        visible={endCycleVisible}
        onClose={() => setEndCycleVisible(false)}
        cycleId={cycle?.id}
        outcomeId={cycle?.outcome?.id ?? null}
        preservationCycleType={cycle?.cycleType}
        // This screen can target a cycle other than the active one, so the modal
        // must preserve *this* cycle's outcome fields rather than the active
        // cycle's. A PATCH replaces the whole sub-object.
        existingOutcome={cycleOutcome}
        transferDate={
          hubPhases.find(
            (p) => p.type === "transfer" || p.type === "insemination",
          )?.startDate ?? null
        }
        hasPregnancyOpportunity={hasConfirmedPregnancyProcedure(hubPhases)}
        preservationSummary={
          endCycleMode === "complete" || hasRecordedPreservationOutcome
            ? cycle?.cycleType === "egg_freezing" ||
              cycle?.cycleType === "embryo_freezing" ||
              cycle?.cycleType === "sperm_freezing"
              ? {
                  treatmentName: displayName,
                  clinicName: cycle.provider ?? null,
                  startDate: cycle.startDate ?? null,
                  cycleNumber,
                  outcomeCounts: preservationOutcomeCounts,
                  specimens: storageGroups.map((group) => ({
                    type: group.type,
                    count: group.specimens.length,
                    facility: group.facility,
                  })),
                  onAddSpecimen: () => {
                    setEndCycleVisible(false);
                    setAddSpecimenVisible(true);
                  },
                }
              : undefined
            : undefined
        }
        onBeforeSave={async () => {
          if (cycle?.id) await createSnapshot.mutateAsync(cycle.id);
        }}
        onSaved={() => {
          // Reset straight to Care overview so the screen never re-renders
          // into its "no active cycle" state — that state's persistent
          // post-cycle dialog would surface the same "we're with you" content
          // the modal just showed, and the user would see the moment twice.
          navigation.reset({
            index: 0,
            routes: [{ name: "CareOverview" }],
          });
        }}
        onOpenArticle={(articleId: string) => {
          const rootNav =
            navigation.getParent<NavigationProp<MainTabParamList>>();
          rootNav?.navigate("Learn", {
            screen: "ArticleDetail",
            params: { articleId, returnTab: "Care" },
          });
        }}
      />
      <AddSpecimenModal
        visible={addSpecimenVisible}
        onClose={() => {
          setAddSpecimenVisible(false);
          setAddSpecimenForRetrieval(false);
        }}
        cycleId={cycle?.id}
        prefill={specimenPrefill}
        onSaved={
          addSpecimenForRetrieval ? handleRecordEggRetrievalOutcome : undefined
        }
      />
      <CryoSpecimenDetailModal
        specimen={
          cryoSpecimenDetails.find(
            (specimen) => specimen.id === selectedSpecimenId,
          ) ?? null
        }
        allSpecimens={cryoSpecimenDetails}
        visible={selectedSpecimenId != null}
        onClose={() => setSelectedSpecimenId(null)}
        getCycleNavigation={(cycleId) => {
          const navigate = resolveCycleNavigation(cycleId);
          if (!navigate) return undefined;
          return () => {
            setSelectedSpecimenId(null);
            navigate();
          };
        }}
      />
      <RecordBetaResultModal
        visible={betaResultVisible}
        onClose={() => {
          setBetaResultVisible(false);
          setBetaResultError(null);
          setBetaTestResult(null);
          fetBetaResultRequestIdRef.current = null;
          betaOutcomeRequestIdRef.current = null;
        }}
        onConfirm={handleConfirmBetaResult}
        onComplete={handleBetaResultComplete}
        transferDate={
          hubPhases.find(
            (p) => p.type === "transfer" || p.type === "insemination",
          )?.startDate ?? null
        }
        isLoading={
          recordCycleOutcome.isPending ||
          createBetaTestResult.isPending ||
          updateBetaTestResult.isPending ||
          recordFETBetaResult.isPending
        }
        error={betaResultError}
        isFETBetaOrder={cycleType === "fet"}
        perspective={perspective}
        partnerDisplayName={partnerDisplayName}
        onOpenArticle={(articleId: string) => {
          const rootNav =
            navigation.getParent<NavigationProp<MainTabParamList>>();
          rootNav?.navigate("Learn", {
            screen: "ArticleDetail",
            params: { articleId, returnTab: "Care" },
          });
        }}
      />
    </GlassScreenLayout>
  );
}

// ─── COMPONENTS ──────────────────────────────────────────────

function CycleSectionTitle({
  title,
  icon,
}: {
  title: string;
  icon?: LucideIcon;
}) {
  return (
    <View style={{ marginTop: 28, marginBottom: 10, marginLeft: 4 }}>
      <SectionTitle icon={icon}>{title}</SectionTitle>
    </View>
  );
}

function NoActiveCycleState({
  onBack,
  onStartTreatment,
  onAddPastCycle,
}: {
  onBack: () => void;
  onStartTreatment: () => void;
  onAddPastCycle: () => void;
}) {
  const t = useTranslations();
  const capturePress = usePressCapture("NoActiveCycleState");

  return (
    <ScrollView
      showsVerticalScrollIndicator={false}
      contentContainerStyle={{
        paddingHorizontal: 16,
        paddingTop: SCREEN_PADDING_TOP,
        paddingBottom: 120,
        maxWidth: 520,
        width: "100%",
        alignSelf: "center",
      }}
    >
      <Pressable
        accessibilityRole="button"
        accessibilityLabel={`Back to ${t("care.overview.screen_title")}`}
        onPress={() => {
          // Arrow-only control; its accessibility label is the copy.
          capturePress(
            pressLabel(`Back to ${t("care.overview.screen_title")}`),
            "back_to_care",
          );
          onBack();
        }}
        hitSlop={8}
        style={{
          flexDirection: "row",
          alignItems: "center",
          gap: 4,
          marginBottom: 8,
        }}
      >
        <ArrowLeft
          size={18}
          color={semanticColors.action.primary.background}
          strokeWidth={1.5}
        />
        <Text
          className="font-dm-sans"
          style={{
            fontSize: 14,
            color: semanticColors.action.primary.background,
          }}
        >
          {t("care.overview.screen_title")}
        </Text>
      </Pressable>

      <ScreenHeader
        title={t("care.cycle_detail.empty.no_active_cycle_title")}
        subtitle={t("care.cycle_detail.empty.no_active_cycle_subtitle")}
      />

      <View style={{ gap: 12 }}>
        <ActionRowCard
          leading={
            <Activity
              size={23}
              color={semanticColors.action.primary.background}
              strokeWidth={1.7}
            />
          }
          title={t("care.start_treatment.title_start")}
          subtitle={t("care.start_treatment.subtitle_step1")}
          onPress={onStartTreatment}
          accessibilityLabel={t("care.start_treatment.title_start")}
        />
        <ActionRowCard
          leading={
            <History
              size={23}
              color={semanticColors.action.primary.background}
              strokeWidth={1.7}
            />
          }
          title={t("care.all_past_cycles.add_button")}
          subtitle={t("care.all_past_cycles.add_subtitle")}
          onPress={onAddPastCycle}
          accessibilityLabel={t("care.all_past_cycles.add_button")}
        />
      </View>
    </ScrollView>
  );
}

type MedForm = "pen" | "vial" | "pill" | "suppository";

const API_FORM_TO_UI: Record<string, MedForm> = {
  pre_filled_pen: "pen",
  pre_filled_syringe: "vial",
  injection_solution: "vial",
  lyophilized_powder: "vial",
  tablet: "pill",
  capsule: "pill",
  oral_solution: "pill",
  sublingual_tablet: "pill",
  vaginal_gel: "suppository",
  vaginal_suppository: "suppository",
  transdermal_patch: "pill",
  nasal_spray: "pill",
  cream: "pill",
};

// ─── Post-cycle / first-time empty state ───────────────────────
// When no active cycle exists, we either show:
//  - An outcome-aware close-out (positive or negative) with a Learn shortcut,
//    for patients who just finished a cycle. Reached by re-entering Cycle
//    Detail after `recordCycleOutcome` closes the cycle.
//  - The original "Aucun cycle actif" placeholder, for first-time users with
//    no cycles at all.

type OutcomeKind =
  "pregnancy" | "no_pregnancy" | "miscarriage" | "cancelled" | "other" | null;

function PostCycleOrEmptyState({
  lastOutcome,
  onBack,
  onBackToCare,
  onOpenArticle,
}: {
  lastOutcome: OutcomeKind;
  onBack: () => void;
  onBackToCare: () => void;
  onOpenArticle: (articleId: string) => void;
}) {
  const t = useTranslations();
  const capturePress = usePressCapture("PostCycleOrEmptyState");
  const isPositive = lastOutcome === "pregnancy";
  const isNegative =
    lastOutcome === "no_pregnancy" ||
    lastOutcome === "miscarriage" ||
    lastOutcome === "cancelled" ||
    lastOutcome === "other";

  if (!isPositive && !isNegative) {
    // First-time user — keep the original placeholder.
    return (
      <View
        style={{
          flex: 1,
          alignItems: "center",
          justifyContent: "center",
          paddingHorizontal: 32,
        }}
      >
        <Pressable
          accessibilityRole="button"
          accessibilityLabel={`Back to ${t("care.overview.screen_title")}`}
          onPress={() => {
            // Arrow-only control; its accessibility label is the copy.
            capturePress(
              pressLabel(`Back to ${t("care.overview.screen_title")}`),
              "back_to_care",
            );
            onBack();
          }}
          style={{
            position: "absolute",
            top: SCREEN_PADDING_TOP,
            left: 20,
            padding: 8,
          }}
        >
          <ArrowLeft size={18} color="rgba(255,255,255,0.6)" />
        </Pressable>
        <ClipboardList size={36} color="#C4B8AE" strokeWidth={1.2} />
        <Text
          className="font-dm-sans"
          style={{
            fontSize: 16,
            fontWeight: "600",
            color: "#9A8E85",
            textAlign: "center",
            marginTop: 16,
            lineHeight: 22,
          }}
        >
          {t("care.cycle_detail.empty.no_active_cycle_title")}
        </Text>
        <Text
          className="font-dm-sans"
          style={{
            fontSize: 13,
            color: "#B0A49B",
            textAlign: "center",
            marginTop: 6,
            lineHeight: 19,
          }}
        >
          {t("care.cycle_detail.empty.no_active_cycle_subtitle")}
        </Text>
      </View>
    );
  }

  const primaryButton = (
    <DialogActions style={{ marginTop: 0 }}>
      <DialogPrimaryButton
        label={t("care.cycle_detail.empty.back_to_care")}
        onPress={onBackToCare}
        flex={1}
        trailing={
          <ChevronRight
            size={16}
            color={semanticColors.action.primary.foreground}
            strokeWidth={2}
            aria-hidden
          />
        }
      />
    </DialogActions>
  );

  if (isPositive) {
    const positiveArticles = getPostCycleArticles(
      "positive",
      t("care.lab_result.tag_post_cycle"),
      t,
    );
    const firstArticle = positiveArticles[0];

    return (
      <Dialog
        visible
        onClose={onBack}
        layout="centered"
        closeButton="minimal"
        containerStyle={{ maxWidth: 440 }}
        dismissOnBackdropTap={false}
        testID="post-cycle-dialog"
      >
        <View style={{ gap: 18 }}>
          <View testID="post-cycle-illustration">
            <OrganicExplanatoryIllustration kind="follicle-cohort" />
          </View>

          <DialogHeader
            title={t("care.cycle_detail.empty.post_cycle_positive_title")}
            subtitle={t("care.cycle_detail.empty.post_cycle_positive_body")}
            style={{ alignItems: "center" }}
            titleStyle={{ textAlign: "center", width: "100%" }}
            subtitleStyle={{ textAlign: "center", width: "100%" }}
          />

          {firstArticle ? (
            <View testID="post-cycle-article-rail">
              <ArticleRailCard
                article={firstArticle}
                read={false}
                onPress={() => onOpenArticle(firstArticle.id)}
                fullWidth
              />
            </View>
          ) : null}

          {primaryButton}
        </View>
      </Dialog>
    );
  }

  return (
    <Dialog
      visible
      onClose={onBack}
      layout="centered"
      closeButton="minimal"
      containerStyle={{ maxWidth: 440 }}
      dismissOnBackdropTap={false}
      testID="post-cycle-dialog"
    >
      <View style={{ gap: 18 }}>
        <View style={{ alignItems: "center", gap: 8 }}>
          <DialogIcon
            source={endingIllustration}
            width={68}
            height={72}
            style={{ marginBottom: 2 }}
          />
          <DialogTitleText style={{ textAlign: "center" }}>
            {t("care.beta_result.header_with_you")}
          </DialogTitleText>
        </View>

        <NegativeCloseoutSupport onOpenArticle={onOpenArticle} />

        {primaryButton}
      </View>
    </Dialog>
  );
}
